import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Row,
  Col,
  Badge,
  Form,
  OverlayTrigger,
  Tooltip,
  Offcanvas,
} from "react-bootstrap";
import { FiInfo, FiSliders } from "react-icons/fi";
import {
  Calendar,
  Search,
  Box,
  Truck,
  CheckCircle,
  Clock,
  FileText,
  Layers,
  X,
  ArrowUpDown,
  Pencil,
  Filter,
  User,
  Building,
  Mail,
  Send,
  Copy,
    Check,

} from "lucide-react";
import NavbarUtility from "./NavbarUtility";

const STATUS_HIERARCHY = {
  ORDINATO: 1,
  RIMESSO: 2,
  "ENTRATO IN MAGAZZINO": 3,
  "BEM INVIATA": 4,
  "BEM COMPLETATA": 5,
  "INVIO ODP/ODPV": 6,
  "COMPLETAMENTO ODP/ODPV": 7,
  EVASO: 8,
};

// NOTA: nome campo prezzo fornitore da confermare - al momento nel JSON fornito
// esiste solo "Prezzo Netto" (prezzo cliente). Cambia questa costante se il campo
// nel tuo dataset reale si chiama diversamente (es. "Prezzo Netto Fornitore",
// "Prezzo Acquisto", ecc.)
const SUPPLIER_PRICE_FIELD = "Prezzo Netto for";

const GOLD = "#ebb51b";
const MONITOR_RED = "rgba(196, 26, 34, 0.97)";
const GRAY = "#9a9ca1";

function MonitoraggioOrdiniIcon({ size = 34, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <rect
        x="7"
        y="10"
        width="50"
        height="35"
        rx="6"
        stroke={MONITOR_RED}
        strokeWidth="4"
      />
      <rect x="21" y="18" width="22" height="17" rx="3" fill={MONITOR_RED} />
      <rect x="29" y="18" width="6" height="8" fill={GRAY} />
      <path
        d="M14 39H21L25 34L30 40L35 36L40 39H50"
        stroke={GRAY}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M26 45V51M38 45V51"
        stroke={GRAY}
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path d="M21 53H43" stroke={MONITOR_RED} strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

const renderTimelineStep = (
  label,
  dateString,
  infoAggiuntiva = "",
  isCurrentStatus = false,
) => {
  const isDone =
    dateString &&
    dateString !== "null" &&
    dateString !== null &&
    dateString !== "(NULL)";
  return (
    <Col className="text-center px-1">
      <div
        style={{
          fontSize: "0.6rem",
          fontWeight: isCurrentStatus ? "900" : "800",
          color: isCurrentStatus ? "#dc3545" : isDone ? "#0d6efd" : "#adb5bd",
          textTransform: "uppercase",
          marginBottom: "4px",
          minHeight: "24px",
        }}
      >
        {label}
      </div>
      {infoAggiuntiva && (
        <div
          style={{ fontSize: "0.55rem", color: "#6c757d", minHeight: "14px" }}
        >
          {isDone ? infoAggiuntiva : ""}
        </div>
      )}
      <div
        style={{
          height: "4px",
          backgroundColor: isCurrentStatus
            ? "#dc3545"
            : isDone
              ? "#0d6efd"
              : "#e9ecef",
          borderRadius: "2px",
          marginTop: "8px",
          marginBottom: "8px",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-5px",
            left: "50%",
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            backgroundColor: isCurrentStatus
              ? "#dc3545"
              : isDone
                ? "#0d6efd"
                : "#dee2e6",
            transform: "translateX(-50%)",
            border: isCurrentStatus ? "2px solid white" : "none",
            boxShadow: isCurrentStatus
              ? "0 0 8px rgba(220, 53, 69, 0.6)"
              : "none",
          }}
        ></div>
      </div>
      <div
        style={{
          fontSize: "0.65rem",
          color: isCurrentStatus ? "#dc3545" : isDone ? "#212529" : "#dee2e6",
          fontWeight: isDone ? "600" : "400",
        }}
      >
        {isDone
          ? new Date(dateString.replace(/-/g, "/")).toLocaleDateString("it-IT")
          : "--"}
      </div>
    </Col>
  );
};

const calculateStatus = (order) => {
  // La nuova query SQL calcola direttamente lo stato di avanzamento.
  const statoSql = String(order?.["STATO AVANZAMENTO"] || "").trim();
  if (statoSql && statoSql !== "(NULL)") return statoSql;
  if (isFullyEvaded(order)) return "EVASO";
  if (order["Data Completamento ODPV"]) return "COMPLETAMENTO ODP/ODPV";
  if (order["Data Invio ODPV"]) return "INVIO ODPV";
  if (order["bem_data_copletamento"]) return "BEM COMPLETATA";
  if (order["bem_data_invio"] || order["id_bem"]) return "BEM INVIATA";
  if (order["data_entrata"] || order["numero_entrata"])
    return "ENTRATO IN MAGAZZINO";
  if (order["Data Rimesso"] || order["numero_rimesso"]) return "RIMESSO";
  return "ORDINATO";
};

// Helper: valida se la causale e' effettivamente valorizzata (non null/(NULL)/vuota)
const hasCausale = (order) => {
  const v = order?.["causale"];
  return v !== null && v !== undefined && v !== "(NULL)" && v !== "";
};

// Helper: valida se un valore "Num Ord Cli" e' effettivamente valorizzato
const hasClientOrder = (order) => {
  const v = order?.["Num Ord Cli"];
  return !!v && v !== "(NULL)" && v !== "null";
};

// TODO: conferma nomi campi reali nel JSON
const QTY_FIELDS = {
  // CLIENTE
  cliOrdinata: "Quantita cli",
  cliEvasa: "Qta EVA cli",
  cliDev: "Qta DEV cli",

  // FORNITORE
  forOrdinata: "Quantita",
  forRimessa: "Qta RIM",
  forEvasa: "Qta EVA",
  forDev: "Qta DEV",
};

const isFullyEvaded = (order) => {
  const qtaOrdCli = parseFloat(order?.[QTY_FIELDS.cliOrdinata] ?? 0);
  const qtaEvaCli = parseFloat(order?.[QTY_FIELDS.cliEvasa] ?? 0);
  const qtaOrdFor = parseFloat(order?.[QTY_FIELDS.forOrdinata] ?? 0);
  const qtaEvaFor = parseFloat(order?.[QTY_FIELDS.forEvasa] ?? 0);

  const cliEvaso = qtaOrdCli > 0 && qtaEvaCli >= qtaOrdCli;
  const forEvaso = qtaOrdFor > 0 && qtaEvaFor >= qtaOrdFor;

  return cliEvaso && forEvaso;
};

// Helper: chiave di raggruppamento righe.
// Ora la chiave combina SEMPRE ordine cliente + ordine fornitore:
// due righe finiscono nello stesso gruppo solo se hanno
// sia lo stesso Num Ord Cli, sia lo stesso ordine fornitore (Num Doc / ID Ordine FOR).
const getGroupKey = (order) => {
  const cliKey = hasClientOrder(order) ? order["Num Ord Cli"] : "NOCLI";

  const forKey =
    order?.["Num Doc"] ||
    order?.["ID Ordine FOR"] ||
    order?.["Articolo"] ||
    "N/D";

  return `CLI-${cliKey}__FOR-${forKey}`;
};

const renderDeliveryBadge = (order) => {
  const deliveryStr = order?.["Data Prev Consegna Cli"];

  if (!deliveryStr) {
    return <span className="badge bg-secondary">—</span>;
  }

  const deliveryDate = new Date(deliveryStr);

  // Data di oggi senza ore
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Data consegna senza ore
  const deliveryDay = new Date(deliveryDate);
  deliveryDay.setHours(0, 0, 0, 0);

  const isOverdue = deliveryDay < today;

  return (
    <span className={`badge ${isOverdue ? "bg-danger" : "bg-success"}`}>
      {deliveryDate.toLocaleDateString("it-IT")}
    </span>
  );
};
// Helper: valida se una stringa data e' effettivamente valorizzata
const isValidDateStr = (d) => !!d && d !== "(NULL)" && d !== "null";

// Helper: restituisce la stringa data piu' recente tra due date (o null se nessuna valida)
const getMostRecentDateStr = (dateStr1, dateStr2) => {
  const v1 = isValidDateStr(dateStr1);
  const v2 = isValidDateStr(dateStr2);
  if (v1 && v2) {
    const d1 = new Date(dateStr1.split(" ")[0]);
    const d2 = new Date(dateStr2.split(" ")[0]);
    return d1 >= d2 ? dateStr1 : dateStr2;
  }
  if (v1) return dateStr1;
  if (v2) return dateStr2;
  return null;
};

const formatDateOrDash = (dateStr) => {
  if (!isValidDateStr(dateStr)) return "--";
  const d = new Date(dateStr.split(" ")[0]);
  return isNaN(d.getTime()) ? "--" : d.toLocaleDateString("it-IT");
};

// Helper: converte una stringa data nel timestamp del solo giorno (senza ora), o null se non valida
const parseDayTimestamp = (dStr) => {
  if (!isValidDateStr(dStr)) return null;
  const datePart = dStr.split(" ")[0].split("T")[0];
  const d = new Date(datePart.replace(/-/g, "/"));
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

const getTodayTimestamp = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
};

// --- LOGICA SEMAFORO (Rosso, Giallo, Verde) ---
// --- STATO TEMPI: usa direttamente STATO ORDINE restituito dalla query SQL ---
const getTrafficLight = (order) => {
  // STATO ORDINE arriva direttamente dalla query SQL come
  // ROSSO / GIALLO / VERDE. Manteniamo anche i vecchi valori
  // DANGER / WARNING / OK per compatibilita con eventuali endpoint legacy.
  const stato = String(order?.["STATO ORDINE"] || "")
    .trim()
    .toUpperCase();

  switch (stato) {
    case "ROSSO":
    case "DANGER":
      return {
        color: "#dc3545",
        label: "DANGER",
        bg: "#ffe3e3",
        key: "ROSSO",
      };

    case "GIALLO":
    case "WARNING":
      return {
        color: "#f59f00",
        label: "WARNING",
        bg: "#fff9db",
        key: "GIALLO",
      };

    case "VERDE":
    case "OK":
      return {
        color: "#2b8a3e",
        label: "OK",
        bg: "#ebfbee",
        key: "VERDE",
      };

    default:
      return {
        color: "#6c757d",
        label: "N/D",
        bg: "#f1f3f5",
        key: "N/D",
      };
  }
};

const TRAFFIC_OPTIONS = [
  { key: "ROSSO", color: "#dc3545", bg: "#ffe3e3", label: "DANGER" },
  { key: "GIALLO", color: "#f59f00", bg: "#fff9db", label: "WARNING" },
  { key: "VERDE", color: "#2b8a3e", bg: "#ebfbee", label: "OK" },
];

const MonitoraggioOrdini = () => {
  const [urgentOrders, setUrgentOrders] = useState([]);
  const [entrateData, setEntrateData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Stato caricamento pagina
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadError, setLoadError] = useState("");

  // FILTRI
  const [showFiltersCanvas, setShowFiltersCanvas] = useState(false);
  const [statusFilter, setStatusFilter] = useState("TUTTI");

  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getDefaultDateRange = () => {
    const oggi = new Date();

    // ESATTAMENTE 180 GIORNI FA
    const dataInizio = new Date(oggi);
    dataInizio.setDate(dataInizio.getDate() - 180);

    return {
      start: getLocalDateString(dataInizio),
      end: getLocalDateString(oggi),
    };
  };

  // Per la chiamata PHP usiamo come limite tecnico il giorno successivo.
  // Questo evita di perdere gli ordini del giorno finale se il backend
  // confronta un timestamp con dataFine alle 00:00:00.
  // Il filtro React continua comunque a mostrare SOLO fino alla data
  // scelta dall'utente, quindi eventuali righe del giorno successivo
  // non vengono visualizzate.
  const addOneDayToDateString = (dateString) => {
    if (!dateString) return "";

    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + 1);

    return getLocalDateString(date);
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [onlyNoClient, setOnlyNoClient] = useState(false);
  const [hideNoClient, setHideNoClient] = useState(false);
  const [onlyWithCausale, setOnlyWithCausale] = useState(false);

  // EVASI PARZIALI: attivo di default.
  // Quando e' attivo nasconde SOLO le righe in cui sia il cliente
  // sia il fornitore hanno Qta DEV = 0.
  const [partialEvadedFilter, setPartialEvadedFilter] = useState(true);

  // Semaforo: ora multi-selezione, vive fuori dal pannello filtri, accanto alla ricerca.
  // Array vuoto = nessun filtro attivo (mostra tutti i colori).
  const [trafficFilters, setTrafficFilters] = useState(["GIALLO"]);
  const [sortBy, setSortBy] = useState("DATE_DESC");
  const [showModal, setShowModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showGiacenzeModal, setShowGiacenzeModal] = useState(false);
  const [giacenze, setGiacenze] = useState([]);
  const [giacenzeLoading, setGiacenzeLoading] = useState(false);
  const [giacenzeError, setGiacenzeError] = useState("");
  const [giacenzeArticolo, setGiacenzeArticolo] = useState("");
  const [articoloPrezzi, setArticoloPrezzi] = useState(null);
  const [giacenzeTab, setGiacenzeTab] = useState('giacenze');
  const [prezziFornitori, setPrezziFornitori] = useState([]);
  const [prezziLoading, setPrezziLoading] = useState(false);
  const [prezziError, setPrezziError] = useState('');

  const formatPrezzoFornitore = (value) => {
    if (value === null || value === undefined || value === '' || value === '(NULL)') return '—';
    const num = Number(String(value).replace(',', '.'));
    return Number.isFinite(num) ? num.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
  };

  // Legge il risultato PHP e mostra l'errore reale se il server restituisce HTML.
  const leggiRispostaArticolo = async (res) => {
    const testo = await res.text();
    let data;
    try {
      data = JSON.parse(testo);
    } catch {
      const titoloHtml = testo.match(/<title[^>]*>([\s\S]*?)<\/title>/i)?.[1]?.replace(/<[^>]*>/g, " ").trim();
      throw new Error(
        `Il server non ha restituito JSON (HTTP ${res.status}). ` +
        (titoloHtml ? `Pagina: ${titoloHtml}. ` : "") +
        `URL: ${res.url}. Verifica che stato_ordini.php aggiornato sia pubblicato e che l'URL non rimandi a una pagina HTML.`
      );
    }
    if (!res.ok || !data.success) {
      throw new Error(data?.error || `Errore PHP (HTTP ${res.status})`);
    }
    return data;
  };

  const loadPrezziFornitori = async (codici) => {
    if (!codici) return;
    setPrezziLoading(true);
    setPrezziError('');
    try {
      const params = new URLSearchParams({ action: 'prezzi_fornitori', ...codici });
const res = await fetch(
  `https://dashboard.technoparts.it/ws/sta_monitoraggio.php?${params}`,
  { cache: "no-store" }
);      const data = await leggiRispostaArticolo(res);
      setPrezziFornitori((Array.isArray(data.prezzi_fornitori) ? data.prezzi_fornitori.flat() : [])
        .sort((a, b) => {
          const na = Number(a.Netto), nb = Number(b.Netto);
          return (Number.isFinite(na) ? na : Infinity) - (Number.isFinite(nb) ? nb : Infinity);
        }));
    } catch (err) {
      setPrezziError(err.message || 'Impossibile caricare i prezzi fornitori');
    } finally {
      setPrezziLoading(false);
    }
  };

  const switchGiacenzeTab = (tab) => {
    setGiacenzeTab(tab);
    if (tab === 'fornitori' && articoloPrezzi && !prezziLoading && !prezziFornitori.length && !prezziError) {
      loadPrezziFornitori(articoloPrezzi);
    }
  };


  const formatQuantita = (value) => {
    if (value === null || value === undefined || value === "" || value === "(NULL)") return "—";
    const numero = Number(String(value).replace(/,/g, "."));
    return Number.isFinite(numero)
      ? Math.trunc(numero).toLocaleString("it-IT", { useGrouping: false })
      : "—";
  };

  // ============================================================
  // MAIL FORNITORE
  // ============================================================
  const [showSupplierMailModal, setShowSupplierMailModal] = useState(false);
  const [supplierMailOrder, setSupplierMailOrder] = useState(null);
  const [supplierEmails, setSupplierEmails] = useState([]);
  const [supplierMailSubject, setSupplierMailSubject] = useState("");
  const [supplierMailMessage, setSupplierMailMessage] = useState("");
  const [supplierMailLoading, setSupplierMailLoading] = useState(false);
  const [supplierMailSending, setSupplierMailSending] = useState(false);
  const [supplierMailError, setSupplierMailError] = useState("");
  const [supplierMailSuccess, setSupplierMailSuccess] = useState("");

  // Scelta destinatario: fornitore / cliente / reparto vendite
  const [showMailChoiceModal, setShowMailChoiceModal] = useState(false);
  const [mailChoiceOrder, setMailChoiceOrder] = useState(null);
  const [mailRecipientType, setMailRecipientType] = useState("supplier");

  const currentStatus = selectedOrder
    ? selectedOrder.calculatedStatus || calculateStatus(selectedOrder)
    : "";

  const [editDates, setEditDates] = useState({});
  const [savingDates, setSavingDates] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const [inlineEdit, setInlineEdit] = useState({
    show: false,
    field: "",
    label: "",
    value: "",
  });

  const [copiedArticle, setCopiedArticle] = useState("");

  const handleCopyArticle = async (e, articolo) => {
    e.stopPropagation();

    if (!articolo) return;

    try {
      await navigator.clipboard.writeText(String(articolo));
      setCopiedArticle(String(articolo));

      setTimeout(() => {
        setCopiedArticle("");
      }, 1500);
    } catch (error) {
      console.error("Errore copia articolo:", error);
    }
  };


const loadOrders = async (start, end) => {
  setLoadingOrders(true);
  setLoadError("");

  try {
    const url =
      `https://dashboard.technoparts.it/monitoraggio.json?_ts=${Date.now()}`;

    const res = await fetch(url, { cache: "no-store" });

    if (!res.ok) {
      throw new Error(
        `Impossibile leggere monitoraggio.json (HTTP ${res.status})`
      );
    }

    const json = await res.json();

    if (!json || !Array.isArray(json.MONITORAGGIO)) {
      throw new Error(
        "monitoraggio.json non contiene un array MONITORAGGIO valido"
      );
    }

    const rows = json.MONITORAGGIO.flat();

    const mapped = rows.map((row) => {
      const order = {
        ...row,

        "Qta RIM":
          row.rim_qta ?? row["Qta RIM"] ?? null,

        bem_data_invio:
          row.rim_max_data_invio ??
          row.mdm_max_data_invio ??
          row.bem_data_invio ??
          null,

        bem_data_copletamento:
          row.rim_max_data_completamento ??
          row.mdm_max_data_completamento ??
          row.bem_data_copletamento ??
          null,

        "Data Invio ODPV":
          row.cli_max_data_invio ??
          row["Data Invio ODPV"] ??
          null,

        "Data Completamento ODPV":
          row.cli_max_data_completamento ??
          row["Data Completamento ODPV"] ??
          null,
      };

      return {
        ...order,
        calculatedStatus: calculateStatus(order),
      };
    });

    setEntrateData([]);

    setUrgentOrders(
      mapped.sort((a, b) =>
        getGroupKey(a).localeCompare(getGroupKey(b))
      )
    );

    console.log(
      "Ordini caricati da monitoraggio.json:",
      mapped.length
    );

  } catch (err) {
    console.error("Errore lettura monitoraggio.json:", err);

    setUrgentOrders([]);
    setEntrateData([]);

    setLoadError(
      err?.message || "Impossibile leggere monitoraggio.json"
    );
  } finally {
    setLoadingOrders(false);
  }
};

  useEffect(() => {
    loadOrders(dateRange.start, dateRange.end);
  }, []);
  const handleOpenGiacenze = async (e, ordine) => {
    e?.stopPropagation();
    const articolo = ordine?.["Articolo"] || ordine?.["Articolo cli"];
    const precodice = ordine?.["Precodice"] || ordine?.["Precodice cli"];
    if (!articolo || !precodice) return;

    setGiacenzeArticolo(`${precodice} - ${articolo}`);
    setArticoloPrezzi({ articolo, precodice });
    setGiacenzeTab('giacenze');
    setPrezziFornitori([]);
    setPrezziError('');
    setGiacenze([]);
    setGiacenzeError("");
    setGiacenzeLoading(true);
    setShowGiacenzeModal(true);
    try {
      const params = new URLSearchParams({ action: "giacenze", articolo, precodice });
      const res = await fetch(`https://dashboard.technoparts.it/ws/stato_ordini.php?${params}`, { cache: "no-store" });
      const data = await leggiRispostaArticolo(res);
      setGiacenze(Array.isArray(data.giacenze) ? data.giacenze.flat() : []);
    } catch (err) {
      setGiacenzeError(err.message || "Impossibile caricare le giacenze");
    } finally {
      setGiacenzeLoading(false);
    }
  };

  const handleOpenModal = async (ordine) => {
    try {
      const idOrdine = ordine["ID Ordine CLI"];

      const res = await fetch(
        `https://dashboard.technoparts.it/ws/test_ordini.php?id_ordine=${idOrdine}`,
      );

      const dettaglio = await res.json();

      console.log("RISPOSTA PHP:", dettaglio);

      if (dettaglio.success && dettaglio.data?.length > 0) {
        setSelectedOrder(dettaglio.data[0]);
      } else {
        setSelectedOrder(ordine);
      }

      setShowModal(true);
    } catch (error) {
      console.error("Errore caricamento dettaglio:", error);

      // fallback al dato della tabella
      setSelectedOrder(ordine);
      setShowModal(true);
    }
  };

  const getMailGroupRows = (ordine) => {
    if (!ordine) return [];

    const key = getGroupKey(ordine);

    return urgentOrders.filter((row) => getGroupKey(row) === key);
  };

  const buildArticlesList = (ordine, mode = "supplier") => {
    const rows = getMailGroupRows(ordine);

    if (!rows.length) return "- Nessun articolo disponibile";

    return rows
      .map((row) => {
        const articolo =
          mode === "client"
            ? row?.["Articolo cli"] || row?.["Articolo"] || "N/D"
            : row?.["Articolo"] || row?.["Articolo cli"] || "N/D";

        const descrizione =
          mode === "client"
            ? row?.["Descrizione cli"] || row?.["Descrizione"] || ""
            : row?.["Descrizione"] || row?.["Descrizione cli"] || "";

        const quantita =
          mode === "client"
            ? (row?.["Quantita cli"] ?? row?.["Quantita"] ?? "")
            : (row?.["Quantita"] ?? row?.["Quantita cli"] ?? "");

        return `- ${articolo}${descrizione ? ` - ${descrizione}` : ""}${
          quantita !== "" ? ` | Q.tà: ${quantita}` : ""
        }`;
      })
      .join("\n");
  };

  const buildMailContent = (ordine, type) => {
    const supplierDoc =
      `${ordine?.["Tipo Doc"] || ""} ${ordine?.["Num Doc"] || "N/D"}`.trim();
    const clientDoc =
      `${ordine?.["Tipo Ord Cli"] || ""} ${ordine?.["Num Ord Cli"] || "N/D"}`.trim();

    if (type === "client") {
      return {
        subject: `Aggiornamento consegna ordine ${clientDoc}`,
        message: `Buongiorno,

con la presente desideriamo informarvi che il seguente ordine sta subendo un ritardo rispetto alle tempistiche previste:

Ordine cliente: ${clientDoc}

Articoli:
${buildArticlesList(ordine, "client")}

Ci scusiamo per il ritardo. Vi aggiorneremo non appena avremo nuove informazioni sulla consegna.

Cordiali saluti
Technoparts`,
      };
    }

    if (type === "sales") {
      return {
        subject: `Sollecito completamento ODPV ordine ${clientDoc}`,
        message: `Buongiorno,

segnaliamo il seguente ordine per il quale risulta necessario verificare/completare la relativa ODPV:

Ordine cliente: ${clientDoc}
Ordine fornitore: ${supplierDoc}

Articoli:
${buildArticlesList(ordine, "client")}

Chiediamo cortesemente di verificare la situazione e procedere con quanto necessario.

Grazie.

Technoparts`,
      };
    }

    return {
      subject: `Richiesta aggiornamento ordine ${supplierDoc}`,
      message: `Buongiorno,

con la presente chiediamo cortesemente un aggiornamento in merito al seguente ordine:

Ordine fornitore: ${supplierDoc}

Articoli:
${buildArticlesList(ordine, "supplier")}

Vi chiediamo gentilmente di comunicarci lo stato dell'ordine e, se disponibile, la data prevista di consegna.

Grazie per la collaborazione.

Cordiali saluti
Technoparts`,
    };
  };

  const openMailChoice = (ordine) => {
    setMailChoiceOrder(ordine);
    setShowMailChoiceModal(true);
  };

  const openMailComposer = async (type) => {
    const ordine = mailChoiceOrder;
    if (!ordine) return;

    const content = buildMailContent(ordine, type);

    setMailRecipientType(type);
    setSupplierMailOrder(ordine);
    setSupplierEmails([]);
    setSupplierMailError("");
    setSupplierMailSuccess("");
    setSupplierMailSubject(content.subject);
    setSupplierMailMessage(content.message);
    setShowMailChoiceModal(false);
    setShowSupplierMailModal(true);
    setSupplierMailLoading(true);

    try {
      const payload =
        type === "supplier"
          ? {
              action: "lookup",
              recipient_type: "supplier",
              id_fornitore: Number(ordine?.["ID Fornitore"] || 0),
            }
          : type === "client"
            ? {
                action: "lookup",
                recipient_type: "client",
                id_cliente: Number(ordine?.["ID Cliente"] || 0),
              }
            : {
                action: "lookup",
                recipient_type: "sales",
              };

      const res = await fetch(
        "https://dashboard.technoparts.it/ws/stato_ordini.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data?.error || `Errore server HTTP ${res.status}`);
      }

      const emails = Array.isArray(data.emails) ? data.emails : [];
      setSupplierEmails(emails);

      if (!emails.length) {
        setSupplierMailError("Nessun indirizzo email valido trovato.");
      }
    } catch (error) {
      console.error("Errore recupero destinatari:", error);
      setSupplierMailError(
        error?.message || "Impossibile recuperare gli indirizzi email.",
      );
    } finally {
      setSupplierMailLoading(false);
    }
  };

  const sendSupplierMail = async () => {
    const ordine = supplierMailOrder;
    const subject = supplierMailSubject.trim();
    const message = supplierMailMessage.trim();

    setSupplierMailError("");
    setSupplierMailSuccess("");

    if (!subject) {
      setSupplierMailError("Inserisci l'oggetto della mail.");
      return;
    }

    if (!message) {
      setSupplierMailError("Inserisci il testo della mail.");
      return;
    }

    if (!supplierEmails.length) {
      setSupplierMailError("Nessun destinatario valido.");
      return;
    }

    setSupplierMailSending(true);

    try {
      const res = await fetch(
        "https://dashboard.technoparts.it/ws/stato_ordini.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "send",
            recipient_type: mailRecipientType,
            id_fornitore: Number(ordine?.["ID Fornitore"] || 0),
            id_cliente: Number(ordine?.["ID Cliente"] || 0),
            subject,
            message,
            tipo_doc: ordine?.["Tipo Doc"] || "",
            num_doc: ordine?.["Num Doc"] || "",
            tipo_doc_cli: ordine?.["Tipo Ord Cli"] || "",
            num_doc_cli: ordine?.["Num Ord Cli"] || "",
          }),
        },
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data?.error || `Errore server HTTP ${res.status}`);
      }

      setSupplierMailSuccess("Mail inviata correttamente.");
    } catch (error) {
      console.error("Errore invio mail:", error);
      setSupplierMailError(
        error?.message || "Errore durante l'invio della mail.",
      );
    } finally {
      setSupplierMailSending(false);
    }
  };

  const toggleTrafficFilter = (key) => {
    setTrafficFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleSaveDates = async () => {
    if (!inlineEdit.field) return;

    setSavingDates(true);
    setSaveMsg("");

    const currentField = inlineEdit.field;
    const currentValue = inlineEdit.value;

    const payload = {
      articolo: selectedOrder["Articolo"],
      id_ordine_for: null,
      id_ordine_cli: null,
      data_conferma_fornitori: null,
      data_effettiva_fornitori: null,
      data_conferma_clienti: null,
      data_effettiva_clienti: null,
    };

    if (
      currentField === "data_conferma_fornitori" ||
      currentField === "data_effettiva_fornitori"
    ) {
      payload.id_ordine_for = selectedOrder["ID Ordine FOR"];
      payload[currentField] = currentValue;
    } else if (
      currentField === "data_conferma_clienti" ||
      currentField === "data_effettiva_clienti"
    ) {
      payload.id_ordine_cli = selectedOrder["ID Ordine CLI"];
      payload[currentField] = currentValue;
    }

    setInlineEdit({ show: false, field: "", label: "", value: "" });

    try {
      const res = await fetch(
        "https://dashboard.technoparts.it/ws/edit-data.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();

      if (data.success) {
        setSaveMsg("✅ Data salvata correttamente");

        setUrgentOrders((prev) =>
          prev.map((o) => {
            if (
              o["ID Ordine CLI"] === selectedOrder["ID Ordine CLI"] &&
              o["Articolo cli"] === selectedOrder["Articolo cli"]
            ) {
              const updatedOrder = { ...o };
              if (currentField === "data_conferma_fornitori")
                updatedOrder["Data Conferma Fornitori"] = currentValue;
              if (currentField === "data_effettiva_fornitori")
                updatedOrder["Data Effettiva Fornitori"] = currentValue;

              updatedOrder.calculatedStatus = calculateStatus(updatedOrder);
              return updatedOrder;
            }
            return o;
          }),
        );
      } else {
        setSaveMsg("❌ Errore: " + (data.error || "sconosciuto"));
      }
    } catch (e) {
      console.error("💥 ERRORE DI RETE:", e);
      setSaveMsg("❌ Errore di rete / Connessione fallita");
    } finally {
      setSavingDates(false);
    }
  };

  // Filtro "Evasi parziali".
  // Se disattivato non esclude nulla.
  // Se attivato esclude SOLO quando entrambe le quantita' da evadere
  // (cliente e fornitore) sono realmente valorizzate e uguali a zero.
  const matchesPartialEvadedFilter = (order) => {
    if (!partialEvadedFilter) return true;

    const qtaDevCli = parseFloat(order?.[QTY_FIELDS.cliDev]);
    const qtaDevFor = parseFloat(order?.[QTY_FIELDS.forDev]);

    const cliIsZero = Number.isFinite(qtaDevCli) && qtaDevCli === 0;
    const forIsZero = Number.isFinite(qtaDevFor) && qtaDevFor === 0;

    return !(cliIsZero && forIsZero);
  };

  const getStatusStyle = (stato) => {
    const map = {
      ORDINATO: {
        bg: "rgba(22, 24, 27, .055)",
        color: "#202124",
        border: "rgba(20, 22, 25, .14)",
        glow: "rgba(0,0,0,.035)",
        icon: <Clock size={12} />,
      },
      RIMESSO: {
        bg: "rgba(22, 24, 27, .055)",
        color: "#202124",
        border: "rgba(20, 22, 25, .14)",
        glow: "rgba(0,0,0,.035)",
        icon: <Layers size={12} />,
      },
      "ENTRATO IN MAGAZZINO": {
        bg: "rgba(22, 24, 27, .055)",
        color: "#202124",
        border: "rgba(20, 22, 25, .14)",
        glow: "rgba(0,0,0,.035)",
        icon: <Box size={12} />,
      },
      "BEM INVIATA": {
        bg: "rgba(22, 24, 27, .055)",
        color: "#202124",
        border: "rgba(20, 22, 25, .14)",
        glow: "rgba(0,0,0,.035)",
        icon: <FileText size={12} />,
      },
      "BEM COMPLETATA": {
        bg: "rgba(22, 24, 27, .055)",
        color: "#202124",
        border: "rgba(20, 22, 25, .14)",
        glow: "rgba(0,0,0,.035)",
        icon: <CheckCircle size={12} />,
      },
      "INVIO ODP/ODPV": {
        bg: "rgba(22, 24, 27, .055)",
        color: "#202124",
        border: "rgba(20, 22, 25, .14)",
        glow: "rgba(0,0,0,.035)",
        icon: <Truck size={12} />,
      },
      "COMPLETAMENTO ODP/ODPV": {
        bg: "rgba(22, 24, 27, .055)",
        color: "#202124",
        border: "rgba(20, 22, 25, .14)",
        glow: "rgba(0,0,0,.035)",
        icon: <CheckCircle size={12} />,
      },
      EVASO: {
        bg: "rgba(196, 26, 34, .10)",
        color: "#c41a22",
        border: "rgba(20, 22, 25, .14)",
        glow: "rgba(0,0,0,.035)",
        icon: <CheckCircle size={12} />,
      },
    };

    return (
      map[stato] || {
        bg: "rgba(22, 24, 27, .055)",
        color: "#202124",
        border: "rgba(20, 22, 25, .14)",
        glow: "rgba(0,0,0,.035)",
        icon: <Layers size={12} />,
      }
    );
  };

  const filteredOrders = urgentOrders.filter((order) => {
    const orderDate = order["Data Ordine cli"]
      ? order["Data Ordine cli"].split(" ")[0]
      : "";
    const s = searchTerm.toLowerCase();

    const matchesSearch =
      order["Num Ord Cli"]?.toString().toLowerCase().includes(s) ||
      order["Num Doc"]?.toString().toLowerCase().includes(s) ||
      order["Articolo cli"]?.toString().toLowerCase().includes(s) ||
      order["Rag Soc Cli"]?.toString().toLowerCase().includes(s) ||
      order["Ragione sociale"]?.toString().toLowerCase().includes(s) ||
      order["ID Cliente"]?.toString().toLowerCase().includes(s);

    const matchesStatus =
      statusFilter === "TUTTI" || order.calculatedStatus === statusFilter;

    const matchesStart = dateRange.start === "" || orderDate >= dateRange.start;
    const matchesEnd = dateRange.end === "" || orderDate <= dateRange.end;

    const hasNoClient =
      !order["ID Cliente"] ||
      order["ID Cliente"] === "(NULL)" ||
      !order["Rag Soc Cli"];
    const matchesNoClient = !onlyNoClient || hasNoClient;
    const matchesHideNoClient = !hideNoClient || !hasNoClient;
    const matchesCausale = !onlyWithCausale || hasCausale(order);

    const traffic = getTrafficLight(order);
    const matchesTraffic =
      trafficFilters.length === 0 || trafficFilters.includes(traffic.key);

    // Evasi parziali: di default nasconde le righe con
    // Qta DEV cli = 0 E Qta DEV fornitore = 0.
    const matchesPartialEvaded = matchesPartialEvadedFilter(order);

    return (
      matchesSearch &&
      matchesStatus &&
      matchesStart &&
      matchesEnd &&
      matchesNoClient &&
      matchesHideNoClient &&
      matchesCausale &&
      matchesTraffic &&
      matchesPartialEvaded
    );
  });

  const sortedAndFilteredOrders = [...filteredOrders].sort((a, b) => {
    if (sortBy === "ALPHA_ASC")
      return String(a["Articolo cli"] || "").localeCompare(
        String(b["Articolo cli"] || ""),
      );
    if (sortBy === "DATE_DESC") {
      return (
        new Date(
          b["Data Ordine cli"] ? b["Data Ordine cli"].split(" ")[0] : 0,
        ) -
        new Date(a["Data Ordine cli"] ? a["Data Ordine cli"].split(" ")[0] : 0)
      );
    }
    if (sortBy === "STATUS_ASC") {
      return (
        (STATUS_HIERARCHY[a.calculatedStatus] || 0) -
        (STATUS_HIERARCHY[b.calculatedStatus] || 0)
      );
    }
    return getGroupKey(a).localeCompare(getGroupKey(b));
  });

  const resetFilters = async () => {
    setStatusFilter("TUTTI");

    const defaultRange = getDefaultDateRange();

    setDateRange(defaultRange);

    setOnlyNoClient(false);
    setHideNoClient(false);
    setOnlyWithCausale(false);

    // Il filtro Evasi parziali e' attivo anche dopo il Reset.
    setPartialEvadedFilter(true);

    setTrafficFilters(["GIALLO"]);
    setSearchTerm("");

    await loadOrders(defaultRange.start, defaultRange.end);
  };

  const renderTimelineStep = (
    label,
    dateString,
    infoAggiuntiva = "",
    isCurrentStatus = false,
  ) => {
    const isDone =
      dateString &&
      dateString !== "null" &&
      dateString !== null &&
      dateString !== "(NULL)";

    const normalizedLabel = String(label || "").toLowerCase();
    let StepIcon = Clock;

    if (normalizedLabel.includes("ordine")) StepIcon = FileText;
    if (normalizedLabel.includes("rimesso")) StepIcon = Layers;
    if (normalizedLabel.includes("entrata")) StepIcon = Box;
    if (normalizedLabel.includes("invio bem")) StepIcon = FileText;
    if (normalizedLabel.includes("bem compl")) StepIcon = CheckCircle;
    if (normalizedLabel.includes("invio odpv")) StepIcon = Truck;
    if (normalizedLabel.includes("compl. odpv")) StepIcon = CheckCircle;

    const cleanLabel = String(label || "").replace(/^\d+\.\s*/, "");
    const formattedDate = isDone
      ? new Date(String(dateString).replace(/-/g, "/")).toLocaleDateString(
          "it-IT",
        )
      : "In attesa";

    return (
      <div
        className={`order-timeline-step ${isDone ? "is-done" : ""} ${
          isCurrentStatus ? "is-current" : ""
        }`}
      >
        <div className="order-timeline-icon">
          <StepIcon size={15} strokeWidth={2.2} />
        </div>
        <div className="order-timeline-label">{cleanLabel}</div>
        <div className="order-timeline-date">{formattedDate}</div>
        <div className="order-timeline-info">
          {isDone && infoAggiuntiva ? infoAggiuntiva : " "}
        </div>
      </div>
    );
  };

  const renderTrafficToggle = () => (
    <div className="d-flex align-items-center gap-1 px-2 traffic-toggle-group orders-traffic-toggle-group">
      {TRAFFIC_OPTIONS.map((opt) => {
        const active = trafficFilters.includes(opt.key);
        return (
          <OverlayTrigger
            key={opt.key}
            placement="top"
            overlay={<Tooltip>{opt.label}</Tooltip>}
          >
            <button
              type="button"
              onClick={() => toggleTrafficFilter(opt.key)}
              className="traffic-toggle-btn"
              style={{
                border: active
                  ? `1.5px solid ${opt.color}`
                  : "1.5px solid transparent",
                backgroundColor: active ? opt.bg : "transparent",
                color: opt.color,
              }}
            >
              <span
                style={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  backgroundColor: opt.color,
                  display: "inline-block",
                  boxShadow: active ? `0 0 6px ${opt.color}` : "none",
                }}
              />
            </button>
          </OverlayTrigger>
        );
      })}
    </div>
  );

  const renderTopSearchBar = () => (
    <div className="d-flex gap-2 align-items-center">
      <div className="position-relative">
        <Search
          size={16}
          className="position-absolute"
          style={{
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#adb5bd",
          }}
        />

        <Form.Control
          size="sm"
          className="border-0 orders-search-control"
          placeholder="Cerca cliente, ordine, articolo, fornitore..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            borderRadius: "10px",
            width: "290px",
            paddingLeft: "38px",
            height: "38px",
            fontSize: "0.8rem",
          }}
        />
      </div>

      {renderTrafficToggle()}

      {/* PULSANTE FILTRI */}
      <Button
        variant="light"
        size="sm"
        className="d-flex align-items-center gap-2 fw-bold orders-filter-btn"
        style={{
          height: "38px",
          borderRadius: "10px",
          border: "1px solid #dee2e6",
          whiteSpace: "nowrap",
        }}
        onClick={() => setShowFiltersCanvas(true)}
      >
        <FiSliders size={15} />
        Filtri
      </Button>
    </div>
  );
  const renderTableContent = (isFullscreen = false) => {
    // Somma l'importo di tutte le righe che condividono lo stesso gruppo
    // (Num Ord Cli, o ordine fornitore quando manca l'ordine cliente)
    // a partire dall'indice indicato (i gruppi sono blocchi contigui nell'array ordinato)
    const calcGroupTotal = (startIndex) => {
      const key = getGroupKey(sortedAndFilteredOrders[startIndex]);
      let total = 0;
      for (let i = startIndex; i < sortedAndFilteredOrders.length; i++) {
        if (getGroupKey(sortedAndFilteredOrders[i]) !== key) break;
        const riga = sortedAndFilteredOrders[i];
        total += Number(riga["Netto cli"]) || 0;
      }
      return total;
    };

    return (
      <Table className="custom-table" responsive="true">
        <thead>
          <tr
            className="text-muted"
            style={{
              fontSize: "0.65rem",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            <th
              onClick={() =>
                setSortBy(sortBy === "ALPHA_ASC" ? "DOC_ASC" : "ALPHA_ASC")
              }
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Articolo
              <ArrowUpDown
                size={12}
                className="ms-1"
                style={{
                  color: sortBy === "ALPHA_ASC" ? "#0d6efd" : "#adb5bd",
                }}
              />
            </th>
            <th className="text-center">Quantità</th>
            <th
              onClick={() =>
                setSortBy(sortBy === "DATE_DESC" ? "DOC_ASC" : "DATE_DESC")
              }
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Data Ord. Cliente{" "}
              <ArrowUpDown
                size={12}
                className="ms-1"
                style={{
                  color: sortBy === "DATE_DESC" ? "#0d6efd" : "#adb5bd",
                }}
              />
            </th>

            <th>Importo Cliente</th>
            {isFullscreen && <th className="text-center">Data Cliente</th>}

            <th>Data Ord. Fornitore</th>
            <th>Importo Fornitore</th>
            {isFullscreen && <th className="text-center">Data Fornitore</th>}

            <th
              onClick={() =>
                setSortBy(sortBy === "STATUS_ASC" ? "DOC_ASC" : "STATUS_ASC")
              }
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Stato Avanzamento{" "}
              <ArrowUpDown
                size={12}
                className="ms-1"
                style={{
                  color: sortBy === "STATUS_ASC" ? "#0d6efd" : "#adb5bd",
                }}
              />
            </th>
            <th
              className="text-center"
              style={{ width: isFullscreen ? "150px" : "30px" }}
            >
              {isFullscreen ? "Stato Tempi" : ""}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredOrders.length === 0 ? (
            <tr>
              <td
                colSpan={isFullscreen ? 10 : 8}
                className="text-center text-muted py-4 small"
              >
                Nessun ordine cliente trovato.
              </td>
            </tr>
          ) : (
            sortedAndFilteredOrders.map((ordine, index) => {
              const groupKey = getGroupKey(ordine);
              const showHeader =
                index === 0 ||
                groupKey !== getGroupKey(sortedAndFilteredOrders[index - 1]);
              const currentStyle = getStatusStyle(ordine.calculatedStatus);
              const traffic = getTrafficLight(ordine);

              const dataFornitore = getMostRecentDateStr(
                ordine["Data Conferma Fornitori"],
                ordine["Data Effettiva Fornitori"],
              );
              const dataCliente = getMostRecentDateStr(
                ordine["Data Prev Consegna Cli"],
                ordine["Data Effettiva Consegna Cli"],
              );

              return (
                <React.Fragment key={index}>
                  {showHeader && (
                    <tr className="doc-header-row">
                      <td colSpan={isFullscreen ? 10 : 8}>
                        <div
                          className="d-flex align-items-center justify-content-between flex-wrap gap-2"
                          style={{ fontSize: "0.75rem", fontWeight: "600" }}
                        >
                          <div className="d-flex fw-bold align-items-center gap-2 flex-wrap">
                            {hasClientOrder(ordine) ? (
                              <>
                                {ordine["Tipo Ord Cli"] || ""}{" "}
                                {ordine["Num Ord Cli"] || "N/D"}
                                <span>|</span>
                              </>
                            ) : (
                              <>
                                <span className="badge bg-secondary-subtle text-secondary fw-bold">
                                  Rif. Ordine Fornitore
                                </span>
                                <span>|</span>
                              </>
                            )}
                            <strong className="text-dark fw-bold d-flex align-items-center gap-1">
                              {ordine["Rag Soc Cli"] &&
                              ordine["Rag Soc Cli"] !== "(NULL)" ? (
                                `${ordine["Rag Soc Cli"]} (${ordine["ID Cliente"]})`
                              ) : (
                                <span className="text-danger fw-bold">
                                  NON ASSEGNATO
                                </span>
                              )}
                            </strong>
                            <span>|</span>
                            Tot: € {calcGroupTotal(index).toFixed(2)}
                          </div>
                          <div className="text-dark d-flex align-items-center gap-2">
                            <span className="d-flex align-items-center gap-1">
                              {ordine["Tipo Doc"] || ""}{" "}
                              {ordine["Num Doc"] || "N/D"}
                            </span>
                            <span className="text-muted">|</span>
                            <span>{ordine["Ragione sociale"] || "N/D"}</span>

                            <button
                              type="button"
                              className="supplier-mail-trigger"
                              onClick={(e) => {
                                e.stopPropagation();
                                openMailChoice(ordine);
                              }}
                              title="Invia comunicazione"
                            >
                              <Mail size={14} />
                              <span>Invia mail</span>
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  <tr
                    className="item-row shadow-sm"
                    onClick={() => handleOpenModal(ordine)}
                  >
                    {/* Articolo */}
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className="fw-bold text-dark"
                          style={{ fontSize: "0.82rem" }}
                        >
                          {ordine["Articolo cli"] || ordine["Articolo"]}
                        </span>

                        <button
                          type="button"
                          className="order-copy-btn"
                          title="Copia codice articolo"
                          onClick={(e) =>
                            handleCopyArticle(
                              e,
                              ordine["Articolo cli"] || ordine["Articolo"],
                            )
                          }
                        >
                          {copiedArticle ===
                          String(
                            ordine["Articolo cli"] || ordine["Articolo"],
                          ) ? (
                            <Check size={13} color="#40c057" />
                          ) : (
                            <Copy size={13} />
                          )}
                        </button>
                        <button
                          type="button"
                          className="order-copy-btn"
                          title="Visualizza giacenze"
                          onClick={(e) => handleOpenGiacenze(e, ordine)}
                        >
                          <Box size={14} />
                        </button>
                      </div>
                    </td>

                    {/* Quantità cliente / fornitore */}
                    <td
                      className="text-center"
                      style={{ fontSize: "0.78rem", fontWeight: 700 }}
                    >
                      {formatQuantita(ordine["Quantita cli"] ?? ordine["Quantita"])}
                    </td>

                    {/* Data Ord. Cliente */}
                    <td style={{ fontSize: "0.78rem" }}>
                      {ordine["Data Ordine cli"]
                        ? new Date(
                            ordine["Data Ordine cli"].split(" ")[0],
                          ).toLocaleDateString("it-IT")
                        : "--"}
                    </td>

                    {/* Importo Cliente */}
                    <td style={{ fontSize: "0.78rem" }}>
                      € {Number(ordine["Netto cli"]).toFixed(2)}
                    </td>

                    {/* Data Cliente (solo fullscreen) */}
                    {isFullscreen && (
                      <td
                        className="text-center"
                        style={{ fontSize: "0.78rem" }}
                      >
                        {formatDateOrDash(dataCliente)}
                      </td>
                    )}

                    {/* Data Ord. Fornitore */}
                    <td style={{ fontSize: "0.78rem" }}>
                      {ordine["Data Ordine"]
                        ? new Date(
                            ordine["Data Ordine"].split(" ")[0],
                          ).toLocaleDateString("it-IT")
                        : "--"}
                    </td>
                    
                    {/* Importo Fornitore */}
                    <td style={{ fontSize: "0.78rem" }}>
                      € {Number(
                        ordine["Importo For"] ??
                        ordine["Netto for"] ??
                        0
                      ).toLocaleString("it-IT", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>


                    {/* Data Fornitore (solo fullscreen) */}
                    {isFullscreen && (
                      <td
                        className="text-center"
                        style={{ fontSize: "0.78rem" }}
                      >
                        {formatDateOrDash(dataFornitore)}
                      </td>
                    )}

                    {/* Stato Avanzamento */}
                    <td>
                      <div
                        className="status-badge"
                        style={{
                          backgroundColor: currentStyle.bg,
                          color: currentStyle.color,
                          border: `1px solid ${currentStyle.border}`,
                          boxShadow: `0 8px 22px ${currentStyle.glow}, inset 0 1px 0 rgba(255,255,255,.08)`,
                          borderRadius: "8px",
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        {currentStyle.icon} {ordine.calculatedStatus}
                      </div>
                    </td>

                    {/* Stato Tempi / Semaforo */}
                    <td
                      className={`text-center${!isFullscreen ? " semaforo-cell-compact" : ""}`}
                    >
                      {isFullscreen ? (
                        <div
                          className="status-badge traffic-status-badge"
                          style={{
                            backgroundColor:
                              traffic.key === "ROSSO"
                                ? "rgba(220,53,69,.13)"
                                : traffic.key === "GIALLO"
                                  ? "rgba(245,159,0,.13)"
                                  : traffic.key === "VERDE"
                                    ? "rgba(43,138,62,.13)"
                                    : "rgba(255,255,255,.055)",
                            color: traffic.color,
                            border: `1px solid ${traffic.color}55`,
                            boxShadow: `0 8px 22px ${traffic.color}14, inset 0 1px 0 rgba(255,255,255,.08)`,
                            borderRadius: "8px",
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                          }}
                        >
                          <span
                            style={{
                              width: "9px",
                              height: "9px",
                              flex: "0 0 9px",
                              borderRadius: "50%",
                              backgroundColor: traffic.color,
                              display: "inline-block",
                              boxShadow: `0 0 7px ${traffic.color}`,
                            }}
                          />
                          {traffic.label}
                        </div>
                      ) : (
                        <span
                          style={{
                            width: "9px",
                            height: "9px",
                            borderRadius: "50%",
                            backgroundColor: traffic.color,
                            display: "inline-block",
                            boxShadow: `0 0 6px ${traffic.color}`,
                          }}
                        />
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })
          )}
        </tbody>{" "}
      </Table>
    );
  };

  const qtaOrdinataCli = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.cliOrdinata] || 0)
    : 0;

  const qtaEvasoCli = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.cliEvasa] || 0)
    : 0;

  const qtaDevCli = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.cliDev] || 0)
    : 0;

  const qtaDaEvadereCli = Math.max(0, qtaOrdinataCli - qtaEvasoCli - qtaDevCli);

  // Quantità rimessa
  const qtaRimessaCli = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.forRimessa] || 0)
    : 0;

  const qtaOrdinataFor = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.forOrdinata] || 0)
    : 0;

  const qtaRimessaFor = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.forRimessa] || 0)
    : 0;

  const qtaEvasoFor = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.forEvasa] || 0)
    : 0;

  const qtaDevFor = selectedOrder
    ? parseFloat(selectedOrder[QTY_FIELDS.forDev] || 0)
    : 0;

  const qtaDaEvadereFor = Math.max(0, qtaOrdinataFor - qtaEvasoFor - qtaDevFor);

  return (
    <div className="orders-page-container w-100">
      <NavbarUtility />
      <div className="orders-page">
        <div className="orders-liquid-header d-flex justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3 min-w-0">
            <div className="orders-icon-shell">
              <MonitoraggioOrdiniIcon size={34} />
            </div>

            <div className="min-w-0">
              <div className="d-flex align-items-center gap-2">
                <span className="orders-liquid-title">
                  MONITORAGGIO ORDINI CLIENTE
                </span>

                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Come funziona questo widget</Tooltip>}
                >
                  <button
                    type="button"
                    className="widget-info-btn"
                    aria-label="Informazioni sul widget Monitoraggio Ordini Cliente"
                    onClick={() => setShowInfoModal(true)}
                  >
                    <FiInfo size={15} />
                  </button>
                </OverlayTrigger>
              </div>

              <div className="orders-liquid-subtitle">
                Stato avanzamento, fornitori e tempi di consegna
              </div>
            </div>
          </div>

          {renderTopSearchBar()}
        </div>
        <style>{`
        :root {
          --mo-gold: #ebb51b;
          --mo-gold-soft: rgba(235, 181, 27, .16);
          --mo-bg: #080a0d;
          --mo-panel: rgba(20, 22, 27, .66);
          --mo-panel-strong: rgba(25, 28, 34, .82);
          --mo-border: rgba(255, 255, 255, .11);
          --mo-border-soft: rgba(255, 255, 255, .065);
          --mo-text: #f5f6f7;
          --mo-muted: #a6a9b0;
        }

        .orders-page {
          position: relative;
          min-height: 100vh;
          width: 100%;
          color: var(--mo-text);
          background:
            radial-gradient(circle at 12% 5%, rgba(235,181,27,.12), transparent 28%),
            radial-gradient(circle at 87% 16%, rgba(120,128,145,.12), transparent 28%),
            linear-gradient(145deg, #090a0d 0%, #0f1116 48%, #08090c 100%);
          overflow-x: hidden;
          isolation: isolate;
          padding: 22px;
        }

        .orders-page::before {
          content: "";
          position: absolute;
          top: 0;
          left: 5%;
          right: 5%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.44), transparent);
          pointer-events: none;
          z-index: 4;
        }

        .orders-page::after {
          content: "";
          position: fixed;
          width: 420px;
          height: 420px;
          border-radius: 50%;
          top: 90px;
          left: -220px;
          background: rgba(235,181,27,.19);
          filter: blur(95px);
          pointer-events: none;
          z-index: 0;
          opacity: .42;
        }

        .orders-liquid-header {
          min-height: 82px;
          padding: 14px 16px;
          margin-bottom: 14px;
          border-radius: 24px;
          border: 1px solid var(--mo-border);
          background:
            linear-gradient(145deg, rgba(255,255,255,.085), rgba(255,255,255,.025)),
            var(--mo-panel);
          box-shadow:
            0 18px 55px rgba(0,0,0,.28),
            inset 0 1px 0 rgba(255,255,255,.12);
          backdrop-filter: blur(24px) saturate(155%);
          -webkit-backdrop-filter: blur(24px) saturate(155%);
          position: relative;
          z-index: 2;
          flex-wrap: wrap;
        }

        .orders-icon-shell {
          width: 52px;
          height: 52px;
          border-radius: 16px;
          display: grid;
          place-items: center;
          flex: 0 0 auto;
          background: linear-gradient(145deg, rgba(255,255,255,.11), rgba(255,255,255,.022));
          border: 1px solid rgba(255,255,255,.12);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.14),
            0 10px 24px rgba(0,0,0,.22);
        }

        .orders-liquid-title {
          color: #f7f7f7;
          font-size: .82rem;
          font-weight: 800;
          letter-spacing: .055em;
          transition: color .18s ease;
          white-space: nowrap;
        }

        .orders-liquid-subtitle {
          margin-top: 3px;
          color: var(--mo-muted);
          font-size: .72rem;
          line-height: 1.3;
        }

        .scroll-area {
          position: relative;
          z-index: 2;
          overflow: auto;
          width: 100%;
          height: calc(100vh - 210px);
          min-height: 420px;
          padding: 14px;
          border-radius: 24px;
          border: 1px solid var(--mo-border);
          background:
            linear-gradient(145deg, rgba(255,255,255,.07), rgba(255,255,255,.018)),
            rgba(20,22,27,.68);
          box-shadow:
            0 18px 55px rgba(0,0,0,.30),
            inset 0 1px 0 rgba(255,255,255,.10);
          backdrop-filter: blur(24px) saturate(155%);
          -webkit-backdrop-filter: blur(24px) saturate(155%);
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,.14) transparent;
        }

        .scroll-area::-webkit-scrollbar {
          width: 7px;
          height: 7px;
        }

        .scroll-area::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,.14);
          border-radius: 999px;
        }

        .custom-table {
          border-collapse: separate;
          border-spacing: 0 6px;
          margin-bottom: 0;
          color: #e9eaec;
          --bs-table-bg: transparent;
          --bs-table-color: #e9eaec;
          --bs-table-border-color: transparent;
        }

        .custom-table > :not(caption) > * > * {
          box-shadow: none !important;
        }

        .custom-table thead th {
          color: #e7edf5 !important;
          background:
            linear-gradient(180deg, rgba(67,79,96,.97), rgba(48,58,72,.97)) !important;
          border-top: 1px solid rgba(255,255,255,.10) !important;
          border-bottom: 1px solid rgba(255,255,255,.12) !important;
          padding: 9px 12px !important;
          font-size: .65rem;
          font-weight: 800;
          letter-spacing: .065em;
          text-transform: uppercase;
          white-space: nowrap;
          position: sticky;
          top: 0;
          z-index: 30;
          box-shadow:
            0 7px 18px rgba(0,0,0,.22),
            inset 0 1px 0 rgba(255,255,255,.08);
          backdrop-filter: blur(18px) saturate(135%);
          -webkit-backdrop-filter: blur(18px) saturate(135%);
        }

        .custom-table thead th:first-child {
          border-top-left-radius: 9px;
          border-bottom-left-radius: 9px;
        }

        .custom-table thead th:last-child {
          border-top-right-radius: 9px;
          border-bottom-right-radius: 9px;
        }

        .doc-header-row td {
          color: #e9f4f6 !important;
          background:
            linear-gradient(145deg, rgba(255,255,255,.075), rgba(255,255,255,.018)),
            rgba(38,67,73,.88) !important;
          border: 1px solid rgba(116,178,190,.18) !important;
          border-radius: 9px;
          padding: 8px 13px !important;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.07),
            0 5px 14px rgba(0,0,0,.12);
        }

        .doc-header-row .text-dark,
        .doc-header-row strong {
          color: #f2f3f4 !important;
        }

        .doc-header-row .text-muted {
          color: #8f949e !important;
        }

        .item-row {
          cursor: pointer;
          transition: transform .16s ease, filter .16s ease;
        }

        .item-row td {
          color: #dfe2e6 !important;
          background: rgba(255,255,255,.028) !important;
          border-top: 1px solid rgba(255,255,255,.055) !important;
          border-bottom: 1px solid rgba(255,255,255,.055) !important;
          padding: 10px 12px !important;
          vertical-align: middle;
          transition: background .16s ease, border-color .16s ease, color .16s ease;
        }

        .item-row td:first-child {
          border-left: 1px solid rgba(255,255,255,.055) !important;
          border-top-left-radius: 12px;
          border-bottom-left-radius: 12px;
        }

        .item-row td:last-child {
          border-right: 1px solid rgba(255,255,255,.055) !important;
          border-top-right-radius: 12px;
          border-bottom-right-radius: 12px;
        }

        .item-row:hover td {
          color: #fff !important;
          background: rgba(255,255,255,.065) !important;
          border-color: rgba(235,181,27,.16) !important;
        }

        .item-row .text-dark {
          color: #f4f5f6 !important;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-height: 27px;
          padding: 5px 10px;
          font-weight: 760;
          font-size: .65rem;
          letter-spacing: .025em;
          text-transform: uppercase;
          white-space: nowrap;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          transition:
            transform .16s ease,
            filter .16s ease,
            border-color .16s ease;
        }

        .item-row:hover .status-badge {
          filter: brightness(1.12);
          transform: translateY(-1px);
        }

        .traffic-status-badge {
          min-height: 27px;
          padding: 5px 10px;
          gap: 6px;
          font-size: .65rem;
          font-weight: 760;
          letter-spacing: .025em;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .semaforo-cell-compact {
          width: 34px !important;
          padding: 10px 6px !important;
        }

        .stato-avanzamento-th { width: auto; }
        .row-cols-7 > * { flex: 0 0 auto; width: 14.2857%; }

        .orders-search-control {
          color: #f5f6f7 !important;
          background:
            linear-gradient(145deg, rgba(255,255,255,.07), rgba(255,255,255,.025)) !important;
          border: 1px solid rgba(255,255,255,.11) !important;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.05) !important;
        }

        .orders-search-control::placeholder {
          color: #7f848d !important;
        }

        .orders-search-control:focus {
          background: rgba(255,255,255,.075) !important;
          border-color: rgba(235,181,27,.58) !important;
          box-shadow: 0 0 0 3px rgba(235,181,27,.11) !important;
        }

        .orders-filter-btn {
          color: #e7e8ea !important;
          background: rgba(255,255,255,.055) !important;
          border: 1px solid rgba(255,255,255,.12) !important;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.05) !important;
        }

        .orders-filter-btn:hover {
          color: #fff !important;
          background: rgba(255,255,255,.095) !important;
          border-color: rgba(235,181,27,.28) !important;
          transform: translateY(-1px);
        }

        .orders-traffic-toggle-group {
          height: 38px;
          background:
            linear-gradient(145deg, rgba(255,255,255,.07), rgba(255,255,255,.025));
          border: 1px solid rgba(255,255,255,.11);
          border-radius: 12px;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.05);
        }

        .traffic-toggle-group { height: 38px; }

        .traffic-toggle-btn {
          border-radius: 8px;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform .15s ease, background .15s ease;
        }

        .traffic-toggle-btn:hover {
          transform: translateY(-1px);
          background-color: rgba(255,255,255,.07) !important;
        }

        .widget-info-btn {
          width: 27px;
          height: 27px;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: 50%;
          background: rgba(255,255,255,.055);
          color: var(--mo-gold);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: transform .15s ease, background .15s ease, border-color .15s ease;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.08);
        }

        .widget-info-btn:hover {
          color: #ffd75d;
          background: rgba(235,181,27,.11);
          border-color: rgba(235,181,27,.28);
          transform: translateY(-1px);
        }

        .canvas-section-title {
          font-size: .72rem;
          font-weight: 760;
          color: #c8cbd1;
          text-transform: uppercase;
          letter-spacing: .075em;
          margin-bottom: 10px;
        }

        .widget-info-backdrop,
        .orders-filter-backdrop {
          background: rgba(2,3,5,.74) !important;
          backdrop-filter: blur(9px);
          -webkit-backdrop-filter: blur(9px);
        }

        .widget-info-backdrop { z-index: 20990 !important; }

        .widget-info-card {
          border: 1px solid rgba(255,255,255,.09);
          border-radius: 16px;
          background:
            linear-gradient(145deg, rgba(255,255,255,.065), rgba(255,255,255,.018)),
            rgba(21,24,29,.78);
          padding: 16px;
          height: 100%;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.08);
        }

        .widget-info-step {
          width: 28px;
          height: 28px;
          min-width: 28px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: .72rem;
          font-weight: 800;
          background: rgba(235,181,27,.14);
          color: var(--mo-gold);
          border: 1px solid rgba(235,181,27,.24);
        }

        .widget-info-label {
          font-size: .68rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .075em;
          color: #a7abb3;
        }

        .widget-info-list {
          margin: 0;
          padding-left: 18px;
          color: #cfd2d7;
          font-size: .82rem;
        }

        .widget-info-list li + li { margin-top: 6px; }

        /* INFO MODAL */
        .orders-info-modal .modal-content,
        .orders-detail-modal .modal-content {
          color: var(--mo-text);
          background:
            radial-gradient(circle at 8% 0%, rgba(235,181,27,.11), transparent 28%),
            linear-gradient(145deg, #0d0f13, #14171c 58%, #0a0c0f);
          border: 1px solid var(--mo-border);
          box-shadow:
            0 28px 90px rgba(0,0,0,.55),
            inset 0 1px 0 rgba(255,255,255,.1);
          overflow: hidden;
        }

        .orders-info-modal .modal-content {
          border-radius: 24px;
        }

        .orders-info-modal .modal-header,
        .orders-info-modal .modal-footer,
        .orders-detail-modal .modal-header {
          color: var(--mo-text);
          background: rgba(255,255,255,.025) !important;
          border-color: var(--mo-border-soft) !important;
        }

        .orders-info-modal .modal-body,
        .orders-detail-modal .modal-body {
          color: #e3e5e8;
          background: transparent !important;
        }

        .orders-info-modal .bg-white,
        .orders-info-modal .bg-light,
        .orders-detail-modal .bg-white,
        .orders-detail-modal .bg-light {
          background:
            linear-gradient(145deg, rgba(255,255,255,.055), rgba(255,255,255,.017)),
            rgba(22,25,30,.72) !important;
        }

        .orders-info-modal .text-dark,
        .orders-detail-modal .text-dark {
          color: #f4f5f6 !important;
        }

        .orders-info-modal .text-muted,
        .orders-detail-modal .text-muted {
          color: #9ba0a9 !important;
        }

        .orders-info-modal .border,
        .orders-detail-modal .border,
        .orders-info-modal .border-top,
        .orders-info-modal .border-bottom,
        .orders-detail-modal .border-top,
        .orders-detail-modal .border-bottom {
          border-color: rgba(255,255,255,.085) !important;
        }

        .orders-info-modal .btn-close,
        .orders-filter-canvas .btn-close {
          filter: invert(1) grayscale(1);
          opacity: .75;
        }

        .orders-info-modal .btn-primary,
        .orders-filter-canvas .btn-primary {
          color: #15130d !important;
          background: linear-gradient(145deg, #f2c544, #ebb51b 58%, #c9960d) !important;
          border-color: rgba(255,220,105,.48) !important;
          font-weight: 750;
        }

        .orders-info-modal .btn-light,
        .orders-detail-modal .btn-light {
          color: #e9eaec !important;
          background: rgba(255,255,255,.06) !important;
          border-color: rgba(255,255,255,.1) !important;
        }

        /* FILTER OFFCANVAS */
        .orders-filter-canvas {
          color: var(--mo-text) !important;
          background:
            radial-gradient(circle at 20% 0%, rgba(235,181,27,.12), transparent 32%),
            linear-gradient(155deg, #101217, #171a20 58%, #0b0d10) !important;
          border-left: 1px solid var(--mo-border) !important;
          box-shadow: -24px 0 65px rgba(0,0,0,.42);
        }

        .orders-filter-canvas .offcanvas-header,
        .orders-filter-canvas .offcanvas-body {
          color: var(--mo-text);
          background: transparent !important;
          border-color: var(--mo-border-soft) !important;
        }

        .orders-filter-canvas .form-control,
        .orders-filter-canvas .form-select {
          color: #f4f5f6 !important;
          background: rgba(255,255,255,.055) !important;
          border: 1px solid rgba(255,255,255,.11) !important;
          color-scheme: dark;
        }

        .orders-filter-canvas .form-select option {
          color: #111;
          background: #f4f4f4;
        }

        .orders-filter-canvas .form-check-label,
        .orders-filter-canvas .fw-semibold {
          color: #d7d9dd !important;
        }

        .orders-filter-canvas .text-muted {
          color: #9399a3 !important;
        }

        .orders-filter-canvas .border-top {
          border-color: var(--mo-border-soft) !important;
          background: transparent !important;
        }

        .orders-filter-canvas .btn-outline-secondary {
          color: #e1e3e6;
          border-color: rgba(255,255,255,.16);
          background: rgba(255,255,255,.04);
        }

        /* DETAIL MODAL */
        .orders-detail-modal .modal-content {
          border-radius: 24px;
        }

        .orders-detail-modal .rounded-3,
        .orders-detail-modal .kpi-card {
          border-color: rgba(255,255,255,.08) !important;
        }

        .orders-detail-modal hr {
          border-color: rgba(255,255,255,.1);
          opacity: 1;
        }

        .orders-detail-modal .canvas-section-title {
          color: #c9ccd2;
        }

        .orders-detail-modal .kpi-card {
          background: rgba(255,255,255,.035) !important;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.05);
        }

        .orders-detail-modal .modal-header .badge {
          box-shadow: inset 0 1px 0 rgba(255,255,255,.16);
        }


        /* ========== DETAIL MODAL — LIQUID GLASS ========== */
        .orders-detail-backdrop {
          background: rgba(2, 3, 5, .84) !important;
          backdrop-filter: blur(13px) saturate(125%);
          -webkit-backdrop-filter: blur(13px) saturate(125%);
        }

        /* Desktop: il modale occupa solo l'altezza reale del contenuto
           e viene centrato perfettamente nella viewport. */
        .orders-detail-modal .modal-dialog.orders-detail-dialog {
          width: calc(100vw - 48px) !important;
          max-width: 1180px !important;
          min-height: 100vh;
          margin: 0 auto !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .orders-detail-content {
          position: relative;
          width: 100%;
          height: auto !important;
          max-height: none !important;
          overflow: hidden;
          border-radius: 30px !important;
          color: #f4f5f7 !important;
          background:
            radial-gradient(circle at 8% -10%, rgba(235,181,27,.17), transparent 32%),
            radial-gradient(circle at 92% 2%, rgba(123,137,164,.12), transparent 30%),
            linear-gradient(145deg, rgba(20,23,29,.98), rgba(9,11,14,.99)) !important;
          border: 1px solid rgba(255,255,255,.12) !important;
          box-shadow:
            0 35px 110px rgba(0,0,0,.66),
            0 0 0 1px rgba(235,181,27,.025),
            inset 0 1px 0 rgba(255,255,255,.12) !important;
        }

        .orders-detail-content::before {
          content: "";
          position: absolute;
          top: 0;
          left: 7%;
          right: 7%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent);
          pointer-events: none;
          z-index: 5;
        }

        .orders-detail-modal .modal-header.order-detail-header {
          position: relative;
          z-index: 2;
          padding: 18px 20px !important;
          background: linear-gradient(145deg, rgba(255,255,255,.055), rgba(255,255,255,.012)) !important;
          border-bottom: 1px solid rgba(255,255,255,.075) !important;
        }

        .order-detail-heading { min-width: 0; display: flex; align-items: center; gap: 13px; }

        .order-detail-icon-shell {
          width: 48px; height: 48px; flex: 0 0 48px; display: grid; place-items: center;
          border-radius: 15px; color: var(--mo-gold);
          background: radial-gradient(circle at 35% 20%, rgba(255,255,255,.15), transparent 45%), rgba(235,181,27,.09);
          border: 1px solid rgba(235,181,27,.20);
          box-shadow: 0 10px 25px rgba(0,0,0,.26), inset 0 1px 0 rgba(255,255,255,.12);
        }

        .order-detail-eyebrow { color: #999fa8; font-size: .62rem; font-weight: 800; letter-spacing: .14em; text-transform: uppercase; }

        .order-detail-article {
          display: inline-flex; align-items: center; min-height: 25px; padding: 3px 9px;
          border-radius: 999px; color: #17130a;
          background: linear-gradient(145deg, #f4cb55, #ebb51b 58%, #c9960d);
          border: 1px solid rgba(255,224,120,.42); box-shadow: inset 0 1px 0 rgba(255,255,255,.35);
          font-size: .68rem; font-weight: 850; letter-spacing: .04em;
        }

        .order-detail-description { max-width: 610px; margin: 3px 0 0; color: #f4f5f7; font-size: 1rem; font-weight: 760; line-height: 1.25; letter-spacing: -.01em; }
        .order-detail-subline { margin-top: 5px; display: flex; flex-wrap: wrap; align-items: center; gap: 7px; color: #9297a0; font-size: .69rem; }
        .order-detail-subline strong { color: #cfd2d7; font-weight: 700; }

        .order-detail-header-actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }

        .order-detail-state-pill,
        .order-detail-delivery {
          min-height: 34px; display: inline-flex; align-items: center; gap: 7px; padding: 6px 10px;
          border-radius: 12px; font-size: .68rem; font-weight: 780; letter-spacing: .02em;
          background: rgba(255,255,255,.045); border: 1px solid rgba(255,255,255,.09);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.06);
        }

        .order-detail-delivery { color: #c8cbd1; }
        .order-detail-delivery .badge { padding: 4px 8px !important; border-radius: 999px !important; font-size: .65rem !important; font-weight: 800 !important; border: 1px solid transparent; }
        .order-detail-delivery .badge.bg-success { color: #8ee3ad !important; background: rgba(70,180,111,.12) !important; border-color: rgba(70,180,111,.22) !important; }
        .order-detail-delivery .badge.bg-danger { color: #ff9999 !important; background: rgba(220,53,69,.13) !important; border-color: rgba(220,53,69,.24) !important; }
        .order-detail-delivery .badge.bg-secondary { color: #b7bbc3 !important; background: rgba(255,255,255,.05) !important; border-color: rgba(255,255,255,.08) !important; }

        .order-detail-close {
          width: 34px; height: 34px; display: inline-grid; place-items: center; flex: 0 0 34px; padding: 0;
          border-radius: 11px; color: #afb3bb; background: rgba(255,255,255,.045); border: 1px solid rgba(255,255,255,.09); transition: .18s ease;
        }
        .order-detail-close:hover { color: #fff; transform: translateY(-1px); background: rgba(255,255,255,.09); border-color: rgba(235,181,27,.20); }

        /* Desktop: niente scroll interno. Il body cresce quanto serve. */
        .orders-detail-modal .modal-body.order-detail-body {
          max-height: none !important;
          height: auto !important;
          overflow: visible !important;
          padding: 14px !important;
          background: radial-gradient(circle at 50% 0%, rgba(255,255,255,.025), transparent 32%), transparent !important;
        }

        .order-detail-section {
          position: relative; overflow: hidden; border-radius: 20px; padding: 14px;
          background: linear-gradient(145deg, rgba(255,255,255,.060), rgba(255,255,255,.016)), rgba(17,20,25,.70);
          border: 1px solid rgba(255,255,255,.085);
          box-shadow: 0 15px 34px rgba(0,0,0,.19), inset 0 1px 0 rgba(255,255,255,.07);
          backdrop-filter: blur(18px) saturate(135%); -webkit-backdrop-filter: blur(18px) saturate(135%);
        }
        .order-detail-section::before { content: ""; position: absolute; top: 0; left: 8%; right: 8%; height: 1px; background: linear-gradient(90deg, transparent, rgba(255,255,255,.24), transparent); pointer-events: none; }

        .order-detail-section-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid rgba(255,255,255,.065); }
        .order-detail-section-title { display: flex; align-items: center; gap: 9px; min-width: 0; color: #e9ebee; font-size: .73rem; font-weight: 800; letter-spacing: .085em; text-transform: uppercase; }
        .order-detail-section-icon { width: 29px; height: 29px; display: grid; place-items: center; flex: 0 0 29px; border-radius: 9px; color: var(--mo-gold); background: rgba(235,181,27,.095); border: 1px solid rgba(235,181,27,.17); }
        .order-detail-section-note { color: #7f858f; font-size: .66rem; }

        .order-timeline-grid { position: relative; display: grid; grid-template-columns: repeat(7, minmax(90px, 1fr)); gap: 8px; overflow-x: auto; padding: 5px 2px 4px; scrollbar-width: none; }
        .order-timeline-grid::-webkit-scrollbar { display: none; }
        .order-timeline-grid::before { content: ""; position: absolute; top: 24px; left: 7%; right: 7%; height: 2px; border-radius: 999px; background: linear-gradient(90deg, rgba(235,181,27,.24), rgba(255,255,255,.06)); }
        .order-timeline-step { position: relative; z-index: 1; min-width: 90px; text-align: center; color: #777d87; }
        .order-timeline-icon { width: 38px; height: 38px; margin: 0 auto 8px; display: grid; place-items: center; border-radius: 12px; color: #727883; background: rgba(11,13,17,.96); border: 1px solid rgba(255,255,255,.08); box-shadow: 0 7px 17px rgba(0,0,0,.30), inset 0 1px 0 rgba(255,255,255,.04); }
        .order-timeline-step.is-done .order-timeline-icon { color: #e9b936; background: linear-gradient(145deg, rgba(235,181,27,.16), rgba(235,181,27,.065)), rgba(12,14,17,.96); border-color: rgba(235,181,27,.22); }
        .order-timeline-step.is-current .order-timeline-icon { color: #17130a; background: linear-gradient(145deg, #f4cb55, #ebb51b 58%, #c9960d); border-color: rgba(255,224,120,.48); box-shadow: 0 0 0 5px rgba(235,181,27,.07), 0 8px 24px rgba(235,181,27,.20), inset 0 1px 0 rgba(255,255,255,.36); }
        .order-timeline-label { color: #9fa4ad; font-size: .62rem; font-weight: 800; text-transform: uppercase; letter-spacing: .045em; white-space: nowrap; }
        .order-timeline-step.is-done .order-timeline-label, .order-timeline-step.is-current .order-timeline-label { color: #e7e9ec; }
        .order-timeline-date { margin-top: 4px; color: #757b85; font-size: .65rem; font-weight: 650; }
        .order-timeline-step.is-done .order-timeline-date { color: #c5c8ce; }
        .order-timeline-step.is-current .order-timeline-date { color: var(--mo-gold); font-weight: 800; }
        .order-timeline-info { min-height: 16px; margin-top: 2px; color: #686e78; font-size: .58rem; white-space: nowrap; }

        .order-detail-data-list { display: flex; flex-direction: column; gap: 3px; }
        .order-detail-data-row { min-height: 39px; display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 7px 8px; border-radius: 11px; transition: background .16s ease; }
        .order-detail-data-row:hover { background: rgba(255,255,255,.035); }
        .order-detail-data-row.is-highlight { background: rgba(235,181,27,.045); border: 1px solid rgba(235,181,27,.08); }
        .order-detail-data-label { display: flex; align-items: center; gap: 7px; color: #8f949e; font-size: .70rem; font-weight: 650; white-space: nowrap; }
        .order-detail-data-label svg { color: #6f7580; }
        .order-detail-data-value { max-width: 65%; color: #e8eaed; font-size: .72rem; font-weight: 720; text-align: right; }
        .order-detail-code { display: inline-flex; align-items: center; padding: 3px 7px; border-radius: 8px; color: #f1c64d; background: rgba(235,181,27,.075); border: 1px solid rgba(235,181,27,.12); font-variant-numeric: tabular-nums; }
        .order-detail-edit-wrap { display: inline-flex; align-items: center; justify-content: flex-end; gap: 7px; }
        .order-detail-edit-btn { width: 27px; height: 27px; display: inline-grid; place-items: center; padding: 0; border-radius: 8px; color: #a7abb3; background: rgba(255,255,255,.045); border: 1px solid rgba(255,255,255,.075); transition: .16s ease; }
        .order-detail-edit-btn:hover { color: var(--mo-gold); background: rgba(235,181,27,.09); border-color: rgba(235,181,27,.18); transform: translateY(-1px); }

        .order-save-feedback { display: flex; justify-content: center; margin: 2px 0 12px; }
        .order-save-pill { display: inline-flex; align-items: center; gap: 7px; padding: 7px 11px; border-radius: 999px; font-size: .68rem; font-weight: 760; border: 1px solid rgba(255,255,255,.08); background: rgba(255,255,255,.045); }
        .order-save-pill.is-loading { color: #e4bd4e; }
        .order-save-pill.is-success { color: #8ee3ad; background: rgba(70,180,111,.09); border-color: rgba(70,180,111,.16); }
        .order-save-pill.is-error { color: #ff9b9b; background: rgba(220,53,69,.09); border-color: rgba(220,53,69,.16); }

        .order-quantity-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
        .order-kpi { position: relative; overflow: hidden; min-height: 82px; padding: 11px; border-radius: 14px; background: rgba(255,255,255,.032); border: 1px solid rgba(255,255,255,.07); box-shadow: inset 0 1px 0 rgba(255,255,255,.045); transition: transform .16s ease, background .16s ease, border-color .16s ease; }
        .order-kpi:hover { transform: translateY(-1px); background: rgba(255,255,255,.048); border-color: rgba(255,255,255,.11); }
        .order-kpi::after { content: ""; position: absolute; width: 70px; height: 70px; right: -30px; bottom: -38px; border-radius: 50%; background: currentColor; opacity: .035; filter: blur(2px); }
        .order-kpi-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
        .order-kpi-label { color: #8d929c; font-size: .63rem; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; }
        .order-kpi-icon { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 9px; color: #a8adb5; background: rgba(255,255,255,.045); border: 1px solid rgba(255,255,255,.065); }
        .order-kpi-value { margin-top: 8px; display: flex; align-items: baseline; gap: 4px; color: #f0f1f3; font-size: 1.2rem; font-weight: 820; letter-spacing: -.025em; }
        .order-kpi-value small { color: #747a84; font-size: .62rem; font-weight: 700; }
        .order-kpi.tone-warning .order-kpi-icon, .order-kpi.tone-warning .order-kpi-value { color: #e8b93e; }
        .order-kpi.tone-warning .order-kpi-icon { background: rgba(235,181,27,.08); border-color: rgba(235,181,27,.13); }
        .order-kpi.tone-success .order-kpi-icon, .order-kpi.tone-success .order-kpi-value { color: #76d89b; }
        .order-kpi.tone-success .order-kpi-icon { background: rgba(70,180,111,.08); border-color: rgba(70,180,111,.13); }
        .order-kpi.tone-info .order-kpi-icon, .order-kpi.tone-info .order-kpi-value { color: #8ca8df; }
        .order-kpi.tone-info .order-kpi-icon { background: rgba(100,135,205,.08); border-color: rgba(100,135,205,.13); }

        /* Tablet / mobile: Bootstrap impila gia' le colonne lg.
           Qui lasciamo scorrere l'intero modale, non il body interno. */
        @media (max-width: 991px) {
          .orders-detail-modal {
            overflow-y: auto !important;
            padding: 12px 0 !important;
          }

          .orders-detail-modal .modal-dialog.orders-detail-dialog {
            width: calc(100vw - 24px) !important;
            max-width: none !important;
            min-height: 0;
            margin: 0 auto !important;
            display: block;
          }

          .orders-detail-content {
            height: auto !important;
            max-height: none !important;
            overflow: hidden !important;
          }

          .orders-detail-modal .modal-body.order-detail-body {
            max-height: none !important;
            overflow: visible !important;
          }

          .order-timeline-grid {
            grid-template-columns: repeat(7, minmax(105px, 1fr));
          }
        }

        @media (max-width: 767px) {
          .orders-detail-modal { padding: 8px 0 !important; }
          .orders-detail-modal .modal-dialog.orders-detail-dialog { width: calc(100vw - 16px) !important; }
          .orders-detail-content { border-radius: 22px !important; }
          .orders-detail-modal .modal-header.order-detail-header { padding: 13px !important; }
          .order-detail-heading { align-items: flex-start; }
          .order-detail-icon-shell { width: 42px; height: 42px; flex-basis: 42px; border-radius: 13px; }
          .order-detail-header-actions { width: 100%; justify-content: flex-start; margin-top: 10px; }
          .order-detail-close { margin-left: auto; }
          .orders-detail-modal .modal-body.order-detail-body { padding: 10px !important; }
          .order-detail-section { padding: 11px; border-radius: 17px; }
          .order-detail-data-row { align-items: flex-start; }
          .order-detail-data-value { max-width: 58%; }
          .order-quantity-grid { grid-template-columns: 1fr; }
        }

        /* LOADING / ERROR */
        .orders-feedback {
          min-height: 300px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 30px 18px;
        }

        .orders-feedback-card {
          width: min(460px, 100%);
          padding: 26px 24px;
          border-radius: 22px;
          text-align: center;
          background:
            linear-gradient(145deg, rgba(255,255,255,.075), rgba(255,255,255,.018)),
            rgba(18,21,26,.72);
          border: 1px solid rgba(255,255,255,.10);
          box-shadow:
            0 20px 55px rgba(0,0,0,.30),
            inset 0 1px 0 rgba(255,255,255,.10);
          backdrop-filter: blur(22px) saturate(150%);
          -webkit-backdrop-filter: blur(22px) saturate(150%);
        }

        .orders-loader {
          width: 42px;
          height: 42px;
          margin: 0 auto 16px;
          border-radius: 50%;
          border: 3px solid rgba(255,255,255,.09);
          border-top-color: var(--mo-gold);
          box-shadow: 0 0 22px rgba(235,181,27,.12);
          animation: ordersSpin .8s linear infinite;
        }

        @keyframes ordersSpin {
          to { transform: rotate(360deg); }
        }

        .orders-feedback-title {
          color: #f5f6f7;
          font-size: .94rem;
          font-weight: 800;
          letter-spacing: -.01em;
        }

        .orders-feedback-text {
          margin-top: 6px;
          color: var(--mo-muted);
          font-size: .78rem;
          line-height: 1.55;
        }

        .orders-error-icon {
          width: 44px;
          height: 44px;
          margin: 0 auto 16px;
          border-radius: 14px;
          display: grid;
          place-items: center;
          color: #ff8787;
          font-size: 1.15rem;
          font-weight: 900;
          background: rgba(224,49,49,.12);
          border: 1px solid rgba(255,107,107,.22);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.07);
        }

        .orders-retry-btn {
          min-height: 36px;
          margin-top: 16px;
          padding: 0 16px;
          border-radius: 11px;
          color: #17130a;
          font-size: .78rem;
          font-weight: 800;
          border: 1px solid rgba(255,220,105,.45);
          background: linear-gradient(145deg, #f2c544, #ebb51b 58%, #c9960d);
          box-shadow:
            0 10px 24px rgba(235,181,27,.15),
            inset 0 1px 0 rgba(255,255,255,.35);
          transition: transform .16s ease, box-shadow .16s ease;
        }

        .orders-retry-btn:hover {
          transform: translateY(-1px);
          box-shadow:
            0 14px 30px rgba(235,181,27,.21),
            inset 0 1px 0 rgba(255,255,255,.4);
        }

        @media (max-width: 991px) {
          .orders-liquid-header {
            align-items: flex-start !important;
          }

          .orders-liquid-header > :last-child {
            width: 100%;
          }

          .orders-liquid-header > :last-child > div:first-child {
            flex: 1 1 auto;
          }

          .orders-search-control {
            width: 100% !important;
          }
        }

        @media (max-width: 767px) {
          .orders-page {
            padding: 12px 10px 24px;
          }

          .orders-liquid-header {
            padding: 12px;
          }

          .orders-icon-shell {
            width: 46px;
            height: 46px;
            border-radius: 14px;
          }

          .orders-liquid-title {
            white-space: normal;
          }

          .scroll-area {
            padding: 9px;
          }
        }

        /* =========================================================
           MAIL FORNITORE
        ========================================================= */

        .supplier-mail-trigger {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          min-height: 28px;
          padding: 5px 10px;
          margin-left: 4px;
          border: 1px solid rgba(235,181,27,.32);
          border-radius: 8px;
          color: #f4d46f;
          background:
            linear-gradient(145deg, rgba(235,181,27,.11), rgba(255,255,255,.025));
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.07),
            0 4px 12px rgba(0,0,0,.12);
          font-size: .68rem;
          font-weight: 760;
          white-space: nowrap;
          cursor: pointer;
          transition: all .18s ease;
        }

        .supplier-mail-trigger:hover {
          color: #fff0b8;
          border-color: rgba(235,181,27,.58);
          background:
            linear-gradient(145deg, rgba(235,181,27,.18), rgba(255,255,255,.045));
          transform: translateY(-1px);
        }

        /* SCELTA DESTINATARIO MAIL */
        .mail-choice-modal .modal-dialog {
          width: calc(100% - 32px);
          max-width: 560px;
          margin-left: auto;
          margin-right: auto;
        }

        .mail-choice-modal .modal-content {
          color: #edf0f4;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: 22px;
          overflow: hidden;
          background:
            radial-gradient(circle at 8% 0%, rgba(235,181,27,.10), transparent 32%),
            linear-gradient(145deg, rgba(255,255,255,.07), rgba(255,255,255,.018)),
            rgba(15,18,23,.88);
          box-shadow: 0 28px 90px rgba(0,0,0,.58), inset 0 1px 0 rgba(255,255,255,.09);
          backdrop-filter: blur(30px) saturate(155%);
          -webkit-backdrop-filter: blur(30px) saturate(155%);
        }

        .mail-choice-modal .modal-header {
          padding: 18px 20px;
          border-color: rgba(255,255,255,.07);
        }

        .mail-choice-modal .modal-body {
          padding: 20px;
        }

        .mail-choice-modal .btn-close {
          filter: invert(1);
          opacity: .72;
        }

        .mail-choice-eyebrow {
          color: #ebb51b;
          font-size: .64rem;
          font-weight: 850;
          letter-spacing: .09em;
          text-transform: uppercase;
        }

        .mail-choice-grid {
          display: grid;
          gap: 10px;
        }

        .mail-choice-card {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 15px 16px;
          text-align: left;
          color: #f3f4f5;
          border: 1px solid rgba(255,255,255,.09);
          border-radius: 14px;
          background: rgba(255,255,255,.035);
          transition: .18s ease;
        }

        .mail-choice-card svg {
          flex: 0 0 auto;
          color: #ebb51b;
        }

        .mail-choice-card strong,
        .mail-choice-card span {
          display: block;
        }

        .mail-choice-card strong {
          font-size: .82rem;
          margin-bottom: 2px;
        }

        .mail-choice-card span {
          color: #9da3ad;
          font-size: .70rem;
        }

        .mail-choice-card:hover:not(:disabled) {
          transform: translateY(-1px);
          border-color: rgba(235,181,27,.42);
          background: rgba(235,181,27,.075);
        }

        .mail-choice-card:disabled {
          opacity: .35;
          cursor: not-allowed;
        }

        /* MODALE MAIL - LIQUID GLASS COMPATTO E CENTRATO */
        .supplier-mail-modal.show {
          display: flex !important;
          align-items: center;
          justify-content: center;
        }

        .supplier-mail-modal.show .modal-dialog {
          margin-top: auto;
          margin-bottom: auto;
        }

        .supplier-mail-modal {
          --bs-modal-margin: 1.75rem;
        }

        .supplier-mail-modal .modal-dialog {
          width: calc(100% - 32px);
          max-width: 590px;
          margin-left: auto;
          margin-right: auto;
        }

        .supplier-mail-modal .modal-content {
          position: relative;
          color: #edf0f4;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: 22px;
          overflow: hidden;
          background:
            radial-gradient(circle at 8% 0%, rgba(235,181,27,.10), transparent 30%),
            linear-gradient(145deg, rgba(255,255,255,.075), rgba(255,255,255,.018)),
            rgba(15,18,23,.84);
          box-shadow:
            0 28px 90px rgba(0,0,0,.58),
            0 0 0 1px rgba(255,255,255,.025),
            inset 0 1px 0 rgba(255,255,255,.10);
          backdrop-filter: blur(30px) saturate(155%);
          -webkit-backdrop-filter: blur(30px) saturate(155%);
        }

        .supplier-mail-modal .modal-content::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            linear-gradient(
              115deg,
              rgba(255,255,255,.075) 0%,
              rgba(255,255,255,.018) 30%,
              transparent 55%
            );
        }

        .supplier-mail-modal .modal-header,
        .supplier-mail-modal .modal-footer {
          position: relative;
          z-index: 1;
          border-color: rgba(255,255,255,.07);
          background: rgba(255,255,255,.018);
          padding: 16px 20px;
        }

        .supplier-mail-modal .modal-body {
          position: relative;
          z-index: 1;
          padding: 24px 26px 26px !important;
          min-height: 430px;
          overflow: visible !important;
        }

        .supplier-mail-modal .modal-title {
          font-weight: 800;
          letter-spacing: .01em;
        }

        .supplier-mail-modal .btn-close {
          filter: invert(1);
          opacity: .7;
        }

        .supplier-mail-meta {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 16px;
        }

        .supplier-mail-meta-card {
          min-width: 0;
          padding: 11px 13px;
          border: 1px solid rgba(255,255,255,.075);
          border-radius: 12px;
          background: rgba(255,255,255,.035);
        }

        .supplier-mail-meta-label {
          display: block;
          margin-bottom: 4px;
          color: #8f96a1;
          font-size: .65rem;
          font-weight: 800;
          letter-spacing: .08em;
          text-transform: uppercase;
        }

        .supplier-mail-meta-value {
          color: #f2f3f5;
          font-size: .82rem;
          font-weight: 700;
          word-break: break-word;
        }

        .supplier-mail-modal .form-label {
          color: #b7bcc5;
          font-size: .72rem;
          font-weight: 760;
          letter-spacing: .035em;
          text-transform: uppercase;
        }

        .supplier-mail-modal .form-control {
          color: #f4f5f6 !important;
          border: 1px solid rgba(255,255,255,.09) !important;
          border-radius: 11px !important;
          background: rgba(255,255,255,.045) !important;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.025) !important;
        }

        .supplier-mail-modal .form-control:focus {
          border-color: rgba(235,181,27,.46) !important;
          box-shadow: 0 0 0 3px rgba(235,181,27,.08) !important;
        }

        .supplier-mail-modal textarea.form-control {
          min-height: 270px;
          max-height: 270px;
          resize: none;
          line-height: 1.55;
          overflow-y: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .supplier-mail-modal textarea.form-control::-webkit-scrollbar {
          width: 0;
          height: 0;
          display: none;
        }

        .supplier-mail-from {
          color: #ebb51b;
        }

        .supplier-mail-alert {
          padding: 10px 12px;
          margin-top: 12px;
          border-radius: 10px;
          font-size: .78rem;
          font-weight: 650;
        }

        .supplier-mail-alert.error {
          color: #ffb3b8;
          border: 1px solid rgba(220,53,69,.25);
          background: rgba(220,53,69,.10);
        }

        .supplier-mail-alert.success {
          color: #a9e8b7;
          border: 1px solid rgba(43,138,62,.25);
          background: rgba(43,138,62,.10);
        }

        .supplier-mail-send-btn {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border: 1px solid rgba(235,181,27,.45) !important;
          color: #17191d !important;
          background: #ebb51b !important;
          font-weight: 800 !important;
        }

        .supplier-mail-send-btn:hover {
          filter: brightness(1.06);
        }

        @media (max-width: 767px) {
          .supplier-mail-modal .modal-dialog {
            width: calc(100% - 24px);
            max-width: none;
            margin-left: auto;
            margin-right: auto;
          }

          .supplier-mail-modal .modal-header,
          .supplier-mail-modal .modal-footer {
            padding: 14px 16px;
          }

          .supplier-mail-modal .modal-body {
            padding: 17px 16px 18px !important;
          }

          .supplier-mail-meta {
            grid-template-columns: 1fr;
          }

          .supplier-mail-trigger span {
            display: none;
          }
        }

        /* COPIA ARTICOLO - LIQUID GLASS */

.order-copy-btn {
  width: 26px;
  height: 26px;
  min-width: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 0;
  border-radius: 8px;

  color: #9a9ca1;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);

  cursor: pointer;
  transition: all 0.18s ease;
}

.order-copy-btn:hover {
  color: #ebb51b;
  background: rgba(235, 181, 27, 0.12);
  border-color: rgba(235, 181, 27, 0.35);
  transform: translateY(-1px);
}

.order-copy-btn:active {
  transform: scale(0.92);
}


        /* GIACENZE MODAL — stesso Liquid Glass degli altri modali */
        .orders-giacenze-modal .modal-dialog { max-width: 790px; }
        .orders-giacenze-modal .modal-content {
          color: #f4f5f7; border-radius: 26px; overflow: hidden;
          background:
            radial-gradient(circle at 8% -10%, rgba(235,181,27,.17), transparent 32%),
            radial-gradient(circle at 92% 2%, rgba(123,137,164,.12), transparent 30%),
            linear-gradient(145deg, rgba(20,23,29,.98), rgba(9,11,14,.99)) !important;
          border: 1px solid rgba(255,255,255,.12);
          box-shadow: 0 35px 110px rgba(0,0,0,.66), inset 0 1px 0 rgba(255,255,255,.12);
        }
        .orders-giacenze-modal .modal-header {
          padding: 20px 23px; border-bottom: 1px solid rgba(255,255,255,.085);
          background: rgba(255,255,255,.035);
        }
        .orders-giacenze-title { display: flex; align-items: center; gap: 12px; }
        .orders-giacenze-icon {
          width: 43px; height: 43px; flex: 0 0 43px; display: grid; place-items: center;
          border-radius: 13px; color: #ebb51b; background: rgba(235,181,27,.095);
          border: 1px solid rgba(235,181,27,.20);
        }
        .orders-giacenze-eyebrow {
          color: #9fa4ad; text-transform: uppercase; letter-spacing: .12em;
          font-weight: 800; font-size: .62rem;
        }
        .orders-giacenze-code { font-size: 1rem; font-weight: 800; color: #f4f5f7; }
        .orders-giacenze-year { font-size: .70rem; color: #9fa4ad; }
        .orders-giacenze-modal .modal-body { padding: 22px; }
        .orders-giacenze-panel {
          overflow: hidden; border-radius: 17px; background: rgba(255,255,255,.032);
          border: 1px solid rgba(255,255,255,.085);
        }
        .orders-giacenze-table { margin-bottom: 0; color: #f4f5f7; font-size: .80rem; }
        .orders-giacenze-table thead th {
          background: rgba(235,181,27,.09) !important; color: #d2d5da !important;
          font-size: .65rem; letter-spacing: .075em; text-transform: uppercase;
          font-weight: 800; padding: 13px 10px; border: 0 !important;
        }
        .orders-giacenze-table tbody td {
          background: transparent !important; color: #e8eaed !important;
          padding: 13px 10px; border: 0 !important;
          border-top: 1px solid rgba(255,255,255,.065) !important;
          font-variant-numeric: tabular-nums;
        }
        .orders-giacenze-table tbody tr:hover td { background: rgba(255,255,255,.055) !important; }
        .orders-giacenze-deposito { color: #ebb51b; font-weight: 800; }
        .orders-giacenze-value { font-weight: 750; }
        .orders-giacenze-disponibile { color: #82d8a3; font-weight: 850; }
        .orders-giacenze-empty { padding: 25px; text-align: center; color: #a9afb8; }
        .orders-giacenze-modal .modal-footer {
          border-top: 1px solid rgba(255,255,255,.085); padding: 14px 22px;
          background: rgba(255,255,255,.018);
        }
        .orders-giacenze-close-btn {
          border: 1px solid rgba(255,255,255,.14); background: rgba(255,255,255,.065);
          color: #e9ebee; border-radius: 10px; padding: 7px 16px; font-weight: 700;
        }
        .orders-giacenze-close-btn:hover { background: rgba(235,181,27,.11); border-color: rgba(235,181,27,.35); color: #fff; }
        @media(max-width:767px) {
          .orders-giacenze-modal .modal-dialog { margin: 9px; }
          .orders-giacenze-modal .modal-header, .orders-giacenze-modal .modal-body { padding: 14px; }
          .orders-giacenze-table { font-size: .71rem; }
          .orders-giacenze-table td, .orders-giacenze-table th { padding: 10px 6px !important; }
        }

        /* MODALE GIACENZE - PIÙ PICCOLO E CENTRATO */
.modal.show:has(.giacenze-modal) .modal-dialog,
.giacenze-modal.modal-dialog {
  width: 90%;
  max-width: 650px !important;
  margin-left: auto !important;
  margin-right: auto !important;
}


/* ==========================================
   GIACENZE MODAL - COME DETTAGLIO ORDINE
========================================== */

.modal-dialog.giacenze-modal-dialog {
  width: calc(100% - 32px) !important;
  max-width: 840px !important;
  min-width: 0 !important;

  margin: 1.75rem auto !important;
  height: auto !important;
  min-height: 0 !important;

  position: relative;
  transform: none;
}

/* CENTRATURA VERTICALE */
.modal-dialog.giacenze-modal-dialog.modal-dialog-centered {
  min-height: calc(100% - 3.5rem) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* CONTENUTO DEL MODALE */
.modal-dialog.giacenze-modal-dialog .modal-content {
  width: 100% !important;
  max-width: 100% !important;
  height: auto !important;
  min-height: 0 !important;

  border-radius: 22px !important;
  overflow: hidden;

  background: linear-gradient(
    135deg,
    rgba(35, 37, 42, 0.97),
    rgba(12, 15, 20, 0.98)
  ) !important;

  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.65) !important;

  backdrop-filter: blur(24px);
}

/* HEADER */
.giacenze-modal-content .modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

/* CORPO */
.giacenze-modal-content .modal-body {
  padding: 22px 24px;
}

/* FOOTER */
.giacenze-modal-content .modal-footer {
  padding: 14px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

/* MOBILE */
@media (max-width: 768px) {
  .modal-dialog.giacenze-modal-dialog {
    width: calc(100% - 24px) !important;
    max-width: none !important;
    margin: 12px auto !important;
  }

  .modal-dialog.giacenze-modal-dialog.modal-dialog-centered {
    min-height: calc(100% - 24px) !important;
  }

  .giacenze-modal-content .modal-header,
  .giacenze-modal-content .modal-body,
  .giacenze-modal-content .modal-footer {
    padding: 16px;
  }
}
      
/* ==============================================================
   MONITORAGGIO — LIQUID GLASS WHITE / BLACK / TECHNOPARTS RED
   Solo presentazione: dati, filtri, azioni ed endpoint invariati.
   ============================================================== */
:root {
  --mo-gold: rgba(196, 26, 34, 0.97);
  --mo-gold-soft: rgba(196,26,34,.10);
  --mo-bg: #f5f6f7;
  --mo-panel: rgba(255,255,255,.76);
  --mo-panel-strong: rgba(255,255,255,.95);
  --mo-border: rgba(18,20,24,.14);
  --mo-border-soft: rgba(18,20,24,.075);
  --mo-text: #161719;
  --mo-muted: #64676d;
}
.orders-page-container { background:#f5f6f7 !important; }
.orders-page {
  color:var(--mo-text) !important;
  background: radial-gradient(circle at 3% 0%, rgba(196,26,34,.065), transparent 31%),
  radial-gradient(circle at 95% 15%, rgba(20,20,20,.045), transparent 30%),
  linear-gradient(140deg,#fff 0%,#f4f5f6 55%,#ededf0 100%) !important;
}
.orders-page::after { background:rgba(196,26,34,.11) !important; }
.orders-page::before { background:linear-gradient(90deg,transparent,rgba(196,26,34,.30),transparent) !important; }
.orders-page .orders-liquid-header,
.orders-page .scroll-area,
.orders-page .orders-feedback-card {
  background:linear-gradient(135deg,rgba(255,255,255,.92),rgba(246,247,248,.72)) !important;
  border:1px solid rgba(20,20,20,.11) !important;
  box-shadow:0 20px 55px rgba(15,17,19,.075), inset 0 1px 0 rgba(255,255,255,.95) !important;
  backdrop-filter:blur(26px) saturate(155%) !important;
  -webkit-backdrop-filter:blur(26px) saturate(155%) !important;
}
.orders-page .orders-liquid-title,
.orders-page .orders-feedback-title {color:#111214 !important;}
.orders-page .orders-liquid-subtitle,
.orders-page .orders-feedback-text {color:#666a70 !important;}
.orders-page .orders-icon-shell {background:rgba(196,26,34,.08) !important;border-color:rgba(196,26,34,.20) !important;}
.orders-page .widget-info-btn,
.orders-page .orders-filter-btn,
.orders-page .orders-search-control,
.orders-page .form-control,
.orders-page .form-select {
  background:rgba(255,255,255,.88) !important;
  color:#17191b !important;
  border:1px solid rgba(20,22,24,.13) !important;
  box-shadow:none !important;
}
.orders-page input::placeholder { color:#898b90 !important; }
.orders-page .orders-search-control:focus,
.orders-page .form-control:focus,
.orders-page .form-select:focus {border-color:rgba(196,26,34,.70) !important;box-shadow:0 0 0 3px rgba(196,26,34,.095) !important;}
.orders-page .custom-table {color:#17191b !important;--bs-table-color:#17191b !important;--bs-table-bg:transparent !important;}
.orders-page .custom-table thead th {
  background:#111214 !important;color:#fff !important;
  border-top:1px solid #111214 !important;border-bottom:1px solid #111214 !important;
  box-shadow:none !important;
}
.orders-page .custom-table thead th:first-child {border-radius:10px 0 0 10px !important;}
.orders-page .custom-table thead th:last-child {border-radius:0 10px 10px 0 !important;}
.orders-page .custom-table tbody tr.item-row td {
  background:rgba(255,255,255,.94) !important;color:#18191c !important;
  border-top:1px solid rgba(18,20,23,.065) !important;
  border-bottom:1px solid rgba(18,20,23,.065) !important;
  box-shadow:none !important;
}
.orders-page .custom-table tbody tr.item-row:hover td { background:#f1f2f4 !important; }
.orders-page .custom-table tbody tr.item-row td .text-dark {color:#151618 !important;}
.orders-page .custom-table tbody .doc-header-row td {
  background:linear-gradient(105deg,#151618,#292b2e) !important;
  border:1px solid rgba(0,0,0,.25) !important;
  color:white !important;
  box-shadow:0 6px 17px rgba(0,0,0,.12) !important;
}
.orders-page .doc-header-row .text-dark,
.orders-page .doc-header-row strong,
.orders-page .doc-header-row .text-muted {color:#fff !important;}
.orders-page .doc-header-row .supplier-mail-trigger {border:1px solid rgba(255,255,255,.28) !important;color:#fff !important;background:rgba(196,26,34,.97) !important;}
.orders-page .order-copy-btn {color:#252629 !important;background:#f5f5f6 !important;border:1px solid #dddddf !important;}
.orders-page .order-copy-btn:hover {color:#c41a22 !important;background:#fff !important;}
.orders-page .status-badge {font-weight:800;}
.orders-page .orders-retry-btn,
.orders-page .btn-primary {background:rgba(196,26,34,.97) !important;color:#fff !important;border-color:#c41a22 !important;}
.orders-page .btn-primary:hover {background:#a8151c !important;}
/* Modali, filtri e card chiari anche fuori dal contenitore page */
.orders-detail-modal .modal-content,
.orders-info-modal .modal-content,
.orders-filter-canvas,
.giacenze-modal-content {
  background:linear-gradient(145deg,rgba(255,255,255,.98),rgba(242,243,245,.96)) !important;
  border:1px solid rgba(15,17,20,.13) !important;
  color:#18191b !important;
  box-shadow:0 30px 85px rgba(0,0,0,.18) !important;
  backdrop-filter:blur(28px) !important;
  -webkit-backdrop-filter:blur(28px) !important;
}
.orders-detail-modal .modal-header,
.orders-info-modal .modal-header,
.orders-filter-canvas .offcanvas-header,
.giacenze-modal-content .modal-header {
  background:#161719 !important;color:#fff !important;
  border-bottom:3px solid rgba(196,26,34,.97) !important;
}
.orders-detail-modal .modal-header *,
.orders-info-modal .modal-header *,
.orders-filter-canvas .offcanvas-header *,
.giacenze-modal-content .modal-header * {color:#fff !important;}
.orders-detail-modal .modal-body,
.orders-info-modal .modal-body,
.orders-filter-canvas .offcanvas-body,
.giacenze-modal-content .modal-body {background:transparent !important;color:#17191b !important;}
.orders-detail-modal .order-detail-section,
.orders-detail-modal .order-kpi,
.orders-detail-modal .order-detail-data-row,
.orders-detail-modal .order-timeline-step,
.giacenze-modal-content .orders-giacenze-panel {
  background:rgba(255,255,255,.8) !important;
  border-color:rgba(18,20,23,.10) !important;
  color:#18191b !important;
}
.orders-detail-modal .order-detail-section-title,
.orders-detail-modal .order-detail-data-value,
.orders-detail-modal .order-kpi-value,
.orders-detail-modal .order-detail-description,
.giacenze-modal-content .orders-giacenze-code {color:#151618 !important;}
.orders-detail-modal .order-detail-data-label,
.orders-detail-modal .order-kpi-label,
.orders-detail-modal .order-timeline-date {color:#60636a !important;}
.orders-detail-modal .order-detail-section-icon,
.orders-detail-modal .order-detail-edit-btn {color:#c41a22 !important;}
.orders-detail-modal .order-detail-close,
.giacenze-modal-content .order-detail-close {background:rgba(255,255,255,.10) !important;color:#fff !important;}
.orders-filter-canvas .btn-close {filter:invert(1) !important;opacity:1 !important;}
.orders-filter-canvas .form-control,
.orders-filter-canvas .form-select,
.orders-detail-modal .form-control {background:#fff !important;color:#191a1d !important;border:1px solid #d8dade !important;}
.orders-filter-canvas .btn-primary,
.orders-info-modal .btn-primary,
.orders-detail-modal .btn-primary {background:rgba(196,26,34,.97) !important;color:#fff !important;border-color:#c41a22 !important;}
.giacenze-modal-content .orders-giacenze-table thead th {background:#161719 !important;color:#fff !important;}
.giacenze-modal-content .orders-giacenze-table tbody td {background:#fff !important;color:#17191b !important;}
/* Evidenza di accessibilità nelle testate */
        /* REFINEMENT: header giallo originale, gruppi grigi, testata a tutta larghezza */
.orders-page .orders-icon-shell {
  background:linear-gradient(145deg,rgba(235,181,27,.16),rgba(255,255,255,.84)) !important;
  border:1px solid rgba(235,181,27,.32) !important;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.94), 0 8px 20px rgba(25,25,25,.075) !important;
}
/* Gruppi ordine: grigio antracite al posto del nero */
.orders-page .custom-table tbody .doc-header-row td {
  background:linear-gradient(115deg,#60646a,#73777e) !important;
  color:#fff !important;
  border:0 !important;
  border-radius:0 !important;
  box-shadow:none !important;
}
.orders-page .doc-header-row .text-dark,
.orders-page .doc-header-row strong,
.orders-page .doc-header-row .text-muted { color:#fff !important; }
/* Nessun padding nella superficie tabella: TH neri da bordo a bordo */
.orders-page .scroll-area {
  padding:0 !important;
  overflow:auto;
}
.orders-page .scroll-area > .table-responsive {
  padding:0 !important;
  margin:0 !important;
  border:0 !important;
  border-radius:0 !important;
}
.orders-page .custom-table {
  width:100% !important;
  margin:0 !important;
  border-collapse:separate !important;
  border-spacing:0 !important;
}
.orders-page .custom-table thead,
.orders-page .custom-table thead tr { margin:0 !important; padding:0 !important; }
.orders-page .custom-table thead th {
  background:#111214 !important;
  color:#fff !important;
  border-radius:0 !important;
  box-shadow:none !important;
}
.orders-page .custom-table thead th:first-child,
.orders-page .custom-table thead th:last-child {border-radius:0 !important;}
.orders-page .custom-table tbody .doc-header-row td {border-top:5px solid #f4f5f6 !important;}
.orders-page .custom-table tbody tr.item-row td {background:#f0f1f3 !important;}
.orders-page .custom-table tbody tr.item-row:nth-of-type(even) td {background:#e8e9ec !important;}
.orders-page .custom-table tbody tr.item-row:hover td {background:#dcdee2 !important;}

.orders-page .custom-table thead th svg {color:#fff !important;opacity:.75;}


/* ===== REFINEMENT 20/10: celle squadrate, header alto, mail neutra ===== */
.orders-page-container { background:#f3f4f5 !important; }
.orders-page {
  background:linear-gradient(135deg,#fafafa 0%,#f1f2f4 55%,#e9ebee 100%) !important;
}
.orders-page::before, .orders-page::after {background:transparent !important;box-shadow:none !important;}
.orders-page .custom-table {border-spacing:0 !important;}
.orders-page .custom-table tbody td,
.orders-page .custom-table tbody .doc-header-row td,
.orders-page .custom-table tbody tr.item-row td,
.orders-page .custom-table tbody tr.item-row td:first-child,
.orders-page .custom-table tbody tr.item-row td:last-child {
  border-radius:0 !important;
  font-weight:750 !important;
}
.orders-page .custom-table tbody td span:not(.status-badge):not(.badge),
.orders-page .custom-table tbody td strong {font-weight:750 !important;}
.orders-page .custom-table thead th {
  padding-top:17px !important;
  padding-bottom:17px !important;
  min-height:53px !important;
  vertical-align:middle !important;
  border-radius:0 !important;
}
/* Il codice articolo e' l'unica scritta rossa della tabella */
.orders-page .custom-table tbody tr.item-row td:first-child .fw-bold.text-dark,
.orders-page .custom-table tbody tr.item-row td:first-child > div > span.fw-bold {
  color:rgba(196,26,34,.97) !important;
  font-weight:850 !important;
}
/* Invia mail grigio chiaro, non rosso */
.orders-page .doc-header-row .supplier-mail-trigger {
  color:#212327 !important;
  background:#f0f1f3 !important;
  border:1px solid #d7d9dd !important;
  box-shadow:none !important;
  border-radius:7px !important;
}
.orders-page .doc-header-row .supplier-mail-trigger:hover {
  background:#fff !important;
  color:#111214 !important;
  border-color:#bfc2c7 !important;
}
/* Scelta destinatario: stessa superficie bianca Liquid Glass */
.mail-choice-modal .modal-dialog {max-width:460px !important;width:calc(100% - 28px) !important;margin-left:auto !important;margin-right:auto !important;}
.mail-choice-modal .modal-content,
.supplier-mail-modal .modal-content {
  background:linear-gradient(145deg,rgba(255,255,255,.98),rgba(240,242,245,.96)) !important;
  border:1px solid rgba(30,32,35,.16) !important;
  color:#191b1e !important;
  box-shadow:0 24px 65px rgba(12,14,18,.23) !important;
  backdrop-filter:blur(26px) saturate(140%) !important;
  -webkit-backdrop-filter:blur(26px) saturate(140%) !important;
  border-radius:16px !important;
}
.mail-choice-modal .modal-header,
.supplier-mail-modal .modal-header {background:#17191b !important;color:#fff !important;border-bottom:0 !important;}
.mail-choice-modal .modal-title,
.supplier-mail-modal .modal-title {color:#fff !important;font-weight:800 !important;font-size:.96rem !important;}
.mail-choice-modal .btn-close,
.supplier-mail-modal .btn-close {filter:invert(1) !important;opacity:.92 !important;}
.mail-choice-modal .modal-body,
.supplier-mail-modal .modal-body {background:transparent !important;color:#191b1e !important;}
.mail-choice-modal .mail-choice-card {
  background:rgba(255,255,255,.8) !important;
  color:#202225 !important;
  border:1px solid #d9dce0 !important;
}
.mail-choice-modal .mail-choice-card strong {color:#18191b !important;}
.mail-choice-modal .mail-choice-card span {color:#64686e !important;}
.supplier-mail-modal {display:flex !important;align-items:center !important;justify-content:center !important;}
.supplier-mail-modal .modal-dialog {
  width:calc(100% - 32px) !important;
  max-width:480px !important;
  margin:auto !important;
  min-height:0 !important;
  display:flex !important;
  align-items:center !important;
}
.supplier-mail-modal .modal-content {width:100% !important;max-height:calc(100dvh - 32px) !important;overflow:hidden !important;}
.supplier-mail-modal .modal-content::before {display:none !important;}
.supplier-mail-modal .modal-header,
.supplier-mail-modal .modal-footer {padding:12px 17px !important;}
.supplier-mail-modal .modal-footer {background:rgba(245,246,248,.94) !important;border-top:1px solid #e0e2e5 !important;}
.supplier-mail-modal .modal-body {padding:15px 18px !important;min-height:0 !important;overflow-y:auto !important;}
.supplier-mail-modal .supplier-mail-meta {gap:8px !important;margin-bottom:10px !important;}
.supplier-mail-modal .supplier-mail-meta-card {padding:8px 10px !important;background:#fff !important;border:1px solid #dfe1e5 !important;}
.supplier-mail-modal .supplier-mail-meta-label,
.supplier-mail-modal .form-label {color:#666b73 !important;}
.supplier-mail-modal .supplier-mail-meta-value {color:#22252a !important;}
.supplier-mail-modal .supplier-mail-from {color:#22252a !important;}
.supplier-mail-modal .form-control {color:#202225 !important;background:#fff !important;border:1px solid #d9dce0 !important;}
.supplier-mail-modal .form-control:focus {border-color:#73777e !important;box-shadow:0 0 0 3px rgba(35,37,40,.08) !important;}
.supplier-mail-modal textarea.form-control {min-height:142px !important;height:172px !important;max-height:205px !important;}
.supplier-mail-modal .supplier-mail-send-btn {background:#17191b !important;border-color:#17191b !important;color:#fff !important;}
.supplier-mail-modal .supplier-mail-alert.error {color:#9e1820 !important;background:#fff0f1 !important;}
.supplier-mail-modal .supplier-mail-alert.success {color:#21683a !important;background:#eef8f0 !important;}
.supplier-mail-modal .modal-footer .btn-outline-light {color:#24262a !important;border-color:#c7cbd0 !important;background:#fff !important;}
@media(max-width:575px){
  .supplier-mail-modal .modal-dialog {max-width:calc(100% - 20px) !important;width:calc(100% - 20px) !important;}
  .supplier-mail-modal .modal-body {padding:12px !important;}
  .supplier-mail-modal .supplier-mail-meta {grid-template-columns:1fr !important;}
}


/* ===== ULTIME MODIFICHE: icona rossa e testate ordini grigio chiaro ===== */
.orders-page .orders-icon-shell {
  background: rgba(196, 26, 34, .075) !important;
  border: 1px solid rgba(196, 26, 34, .24) !important;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.95), 0 6px 18px rgba(0,0,0,.06) !important;
}
.orders-page .custom-table tbody .doc-header-row td {
  background: linear-gradient(110deg, #a4a8ae 0%, #babec3 100%) !important;
  color: #191b1e !important;
  border-top: 5px solid #f4f5f6 !important;
}
.orders-page .doc-header-row .text-dark,
.orders-page .doc-header-row strong,
.orders-page .doc-header-row .text-muted,
.orders-page .doc-header-row .d-flex {
  color: #191b1e !important;
}


/* HEADER MONITORAGGIO ORDINI CLIENTE */
.orders-page .orders-liquid-header {
  background: transparent !important;
  background-image: none !important;

  border: none !important;
  border-bottom: 3px solid #c41a22 !important;

  border-radius: 0 !important;
  box-shadow: none !important;
}



.giacenze-modal-dialog-wide { width:min(96vw, 1450px) !important; max-width:1450px !important; }
.orders-article-tabs { display:flex; gap:8px; margin-bottom:16px; padding:4px; border-radius:12px; background:#ededee; }
.orders-article-tab { flex:1; border:0; background:transparent; color:#575b62; border-radius:9px; padding:10px 14px; font-size:.83rem; font-weight:750; display:flex; align-items:center; justify-content:center; gap:8px; }
.orders-article-tab.active { background:#17191b; color:#fff; box-shadow:0 2px 8px #0002; }
.orders-fornitori-panel { min-width:0; }
.orders-fornitori-count { font-size:.73rem; color:#6b7280; margin:0 0 8px; }
.orders-fornitori-scroll { overflow:auto; max-height:52vh; border-radius:9px; }
.orders-fornitori-table { min-width:1190px; font-size:.72rem; white-space:nowrap; }
.orders-fornitori-table thead th { position:sticky; top:0; z-index:2; }
.orders-fornitori-table .orders-fornitori-name { font-weight:650; max-width:235px; overflow:hidden; text-overflow:ellipsis; }
.orders-fornitori-table .orders-fornitori-netto { font-weight:850; color:#127045 !important; }

        `}</style>
        <div className="scroll-area">
          {loadingOrders ? (
            <div className="orders-feedback">
              <div className="orders-feedback-card">
                <div className="orders-loader" />
                <div className="orders-feedback-title">
                  Stiamo caricando i dati degli ordini
                </div>
                <div className="orders-feedback-text">
                  Recupero stato avanzamento, fornitori e tempi di consegna...
                </div>
              </div>
            </div>
          ) : loadError ? (
            <div className="orders-feedback">
              <div className="orders-feedback-card">
                <div className="orders-error-icon">!</div>
                <div className="orders-feedback-title">
                  Errore durante il caricamento
                </div>
                <div className="orders-feedback-text">{loadError}</div>
                <button
                  type="button"
                  className="orders-retry-btn"
                  onClick={() => loadOrders(dateRange.start, dateRange.end)}
                >
                  Riprova
                </button>
              </div>
            </div>
          ) : (
            renderTableContent(true)
          )}
        </div>
        {/* MODALE INFORMAZIONI WIDGET */}
        <Modal
          show={showInfoModal}
          className="p-5 orders-info-modal"
          onHide={() => setShowInfoModal(false)}
          centered
          size="lg"
          scrollable
          style={{ zIndex: 21000 }}
          backdropClassName="widget-info-backdrop"
        >
          <Modal.Header
            closeButton
            className="border-bottom bg-white px-4 py-3"
          >
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0"
                style={{ width: "40px", height: "40px" }}
              >
                <FiInfo size={20} />
              </div>
              <div>
                <Modal.Title
                  className="fw-bold"
                  style={{ fontSize: "1.05rem" }}
                >
                  Come usare Monitoraggio Ordini Cliente
                </Modal.Title>
                <div
                  className="text-muted mt-1"
                  style={{ fontSize: "0.78rem" }}
                >
                  Guida rapida per capire cosa mostra il widget, come navigarlo
                  e come usare ricerca, filtri e dettaglio ordine.
                </div>
              </div>
            </div>
          </Modal.Header>

          <Modal.Body className="bg-light p-4">
            <div className="bg-primary-subtle border border-primary-subtle rounded-4 p-3 mb-3">
              <div className="fw-bold text-primary mb-1">A cosa serve</div>
              <div
                className="text-dark"
                style={{ fontSize: "0.84rem", lineHeight: 1.55 }}
              >
                Questo widget raccoglie in un'unica vista gli ordini cliente
                collegati ai relativi ordini fornitore. Ti permette di
                controllare rapidamente articolo, date, importi, stato di
                avanzamento e rispetto dei tempi, per poi aprire il dettaglio
                completo della singola riga quando serve approfondire.
              </div>
            </div>

            <Row className="g-3 mb-3">
              <Col md={6}>
                <div className="widget-info-card">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="widget-info-step">1</span>
                    <div>
                      <div className="fw-bold text-dark">
                        Aprire e navigare il widget
                      </div>
                      <div
                        className="text-muted"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Vista compatta e vista estesa
                      </div>
                    </div>
                  </div>
                  <ul className="widget-info-list">
                    <li>
                      La pagina mostra direttamente l'elenco completo degli
                      ordini.
                    </li>
                    <li>
                      Sono sempre visibili anche le colonne{" "}
                      <strong>Data Cliente</strong>e{" "}
                      <strong>Data Fornitore</strong>.
                    </li>
                    <li>
                      Clicca su una riga dell'ordine per aprire la scheda di
                      dettaglio.
                    </li>
                  </ul>
                </div>
              </Col>

              <Col md={6}>
                <div className="widget-info-card">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="widget-info-step">2</span>
                    <div>
                      <div className="fw-bold text-dark">Ricerca veloce</div>
                      <div
                        className="text-muted"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Trova subito la riga che ti interessa
                      </div>
                    </div>
                  </div>
                  <ul className="widget-info-list">
                    <li>Usa la barra di ricerca in alto a destra.</li>
                    <li>
                      Puoi cercare per <strong>numero ordine cliente</strong>,{" "}
                      <strong>numero documento fornitore</strong>,{" "}
                      <strong>articolo</strong>, <strong>cliente</strong>,{" "}
                      <strong>fornitore</strong> o <strong>ID cliente</strong>.
                    </li>
                    <li>
                      La ricerca si combina con tutti gli altri filtri attivi.
                    </li>
                  </ul>
                </div>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={6}>
                <div className="widget-info-card">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="widget-info-step">3</span>
                    <div>
                      <div className="fw-bold text-dark">
                        Semaforo dei tempi
                      </div>
                      <div
                        className="text-muted"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Filtro rapido a tre colori
                      </div>
                    </div>
                  </div>
                  <div
                    className="d-flex flex-column gap-2"
                    style={{ fontSize: "0.82rem" }}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <span
                        className="mt-1 rounded-circle flex-shrink-0"
                        style={{
                          width: "10px",
                          height: "10px",
                          background: "#dc3545",
                        }}
                      />
                      <div>
                        <strong>Rosso — Nessuna Data:</strong> segnala
                        situazioni senza date fornitore utili al monitoraggio.
                      </div>
                    </div>
                    <div className="d-flex align-items-start gap-2">
                      <span
                        className="mt-1 rounded-circle flex-shrink-0"
                        style={{
                          width: "10px",
                          height: "10px",
                          background: "#f59f00",
                        }}
                      />
                      <div>
                        <strong>Giallo — In Ritardo:</strong> indica che la data
                        odierna ha superato le date di riferimento disponibili.
                      </div>
                    </div>
                    <div className="d-flex align-items-start gap-2">
                      <span
                        className="mt-1 rounded-circle flex-shrink-0"
                        style={{
                          width: "10px",
                          height: "10px",
                          background: "#2b8a3e",
                        }}
                      />
                      <div>
                        <strong>Verde — Nei Tempi / Evaso:</strong> indica un
                        ordine nei tempi oppure già completato/evaso.
                      </div>
                    </div>
                    <div
                      className="text-muted pt-1"
                      style={{ fontSize: "0.75rem" }}
                    >
                      Puoi selezionare anche più colori insieme. Se non
                      selezioni nessun colore, vengono mostrati tutti.
                    </div>
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div className="widget-info-card">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="widget-info-step">4</span>
                    <div>
                      <div className="fw-bold text-dark">Pannello Filtri</div>
                      <div
                        className="text-muted"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Aprilo dal pulsante “Filtri”
                      </div>
                    </div>
                  </div>
                  <ul className="widget-info-list">
                    <li>
                      <strong>Data Ordine Cliente:</strong> imposta un
                      intervallo data da/a.
                    </li>
                    <li>
                      <strong>Stato Avanzamento:</strong> filtra per Ordinato,
                      Rimesso, Entrato in magazzino, BEM, ODP/ODPV o Evaso.
                    </li>
                    <li>
                      <strong>Cliente Assegnato:</strong> mostra solo ordini
                      senza cliente oppure nascondili.
                    </li>
                    <li>
                      <strong>Evasi parziali:</strong> attivo di default;
                      nasconde le righe in cui cliente e fornitore hanno
                      entrambi Da Evadere uguale a 0.
                    </li>
                    <li>
                      <strong>Causale:</strong> mostra solo gli ordini che hanno
                      una causale valorizzata.
                    </li>
                    <li>
                      <strong>Reset</strong> ripristina i filtri;{" "}
                      <strong>Applica</strong> chiude il pannello mantenendo le
                      scelte effettuate.
                    </li>
                  </ul>
                </div>
              </Col>
            </Row>

            <div className="widget-info-card mb-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="widget-info-step">5</span>
                <div>
                  <div className="fw-bold text-dark">
                    Come leggere la tabella
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                    Dati principali e raggruppamento degli ordini
                  </div>
                </div>
              </div>
              <Row className="g-3">
                <Col md={6}>
                  <div className="widget-info-label mb-2">
                    Intestazione del gruppo
                  </div>
                  <ul className="widget-info-list">
                    <li>
                      Ordine cliente, quando presente; altrimenti viene indicato
                      il riferimento all'ordine fornitore.
                    </li>
                    <li>Cliente e relativo ID.</li>
                    <li>Totale economico del gruppo.</li>
                    <li>Documento e ragione sociale del fornitore.</li>
                  </ul>
                </Col>
                <Col md={6}>
                  <div className="widget-info-label mb-2">Riga articolo</div>
                  <ul className="widget-info-list">
                    <li>Articolo.</li>
                    <li>Data e importo dell'ordine cliente.</li>
                    <li>Data e importo dell'ordine fornitore.</li>
                    <li>Stato di avanzamento calcolato.</li>
                    <li>Stato tempi, rappresentato dal semaforo.</li>
                  </ul>
                </Col>
              </Row>
              <div
                className="mt-3 p-2 px-3 rounded-3 bg-light border"
                style={{ fontSize: "0.78rem" }}
              >
                <strong>Ordinamento:</strong> clicca sulle intestazioni{" "}
                <strong>Articolo</strong>, <strong>Data Ord. Cliente</strong> o{" "}
                <strong>Stato Avanzamento</strong> per cambiare l'ordinamento.
              </div>
            </div>

            <div className="widget-info-card">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="widget-info-step">6</span>
                <div>
                  <div className="fw-bold text-dark">
                    Cosa trovi nel dettaglio ordine
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                    Apri il dettaglio cliccando una riga
                  </div>
                </div>
              </div>
              <Row className="g-3">
                <Col md={6}>
                  <ul className="widget-info-list">
                    <li>
                      <strong>Timeline logistica:</strong> Ordine, Rimesso,
                      Entrata magazzino, Invio/Completamento BEM,
                      Invio/Completamento ODPV.
                    </li>
                    <li>
                      <strong>Dati Cliente:</strong> ordine, cliente, data
                      ordine e date di consegna concordata, prevista ed
                      effettiva.
                    </li>
                    <li>
                      <strong>Dati Fornitore:</strong> ordine, fornitore, data
                      ordine, data conferma e data effettiva.
                    </li>
                  </ul>
                </Col>
                <Col md={6}>
                  <ul className="widget-info-list">
                    <li>
                      <strong>Quantità Cliente:</strong> ordinata, da evadere,
                      evasa e rimessa.
                    </li>
                    <li>
                      <strong>Quantità Fornitore:</strong> ordinata, da evadere,
                      evasa e rimessa.
                    </li>
                    <li>
                      Le date che mostrano l'icona a forma di matita possono
                      essere aperte in modifica.
                    </li>
                  </ul>
                </Col>
              </Row>
            </div>

            {/* SEZIONE 7 - SIGNIFICATO DELLE DATE */}
            <div className="widget-info-card mt-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="widget-info-step">7</span>
                <div>
                  <div className="fw-bold text-dark">
                    Significato delle date
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                    Come vengono lette e calcolate le principali date di
                    fornitura e consegna
                  </div>
                </div>
              </div>

              <div
                className="p-3 rounded-3 bg-light border mb-3"
                style={{ fontSize: "0.8rem", lineHeight: 1.55 }}
              >
                <strong>Regola generale:</strong> per le date fornitore il
                sistema utilizza prima la data specifica della{" "}
                <strong>riga ordine</strong>. Se non disponibile, utilizza la
                data presente nella
                <strong> testata dell’ordine fornitore</strong>. Le date cliente
                calcolate tengono inoltre conto dei{" "}
                <strong>giorni di spedizione</strong>
                configurati per il fornitore.
              </div>

              <Row className="g-3">
                <Col md={6}>
                  <div className="widget-info-label mb-2">Date Fornitore</div>
                  <ul className="widget-info-list">
                    <li>
                      <strong>Data Conferma Fornitori:</strong> prima data di
                      consegna confermata dal fornitore. Il sistema usa la data
                      della riga ordine e, se assente, quella della testata
                      ordine.
                    </li>
                    <li>
                      <strong>Data Effettiva Fornitori:</strong> data aggiornata
                      associata alla consegna del fornitore. Anche in questo
                      caso viene privilegiato il valore della riga e, se non
                      presente, quello della testata.
                    </li>
                    <li>
                      <strong>Data proposta:</strong> data proposta sull’ordine
                      fornitore utilizzata come riferimento per il controllo dei
                      tempi. Se la data è superata, il sistema può segnalare
                      l’ordine in ritardo.
                    </li>
                  </ul>
                </Col>

                <Col md={6}>
                  <div className="widget-info-label mb-2">Date Cliente</div>
                  <ul className="widget-info-list">
                    <li>
                      <strong>Data Prev Consegna Cli:</strong> data prevista di
                      spedizione/consegna registrata sull’ordine cliente.
                    </li>
                    <li>
                      <strong>Data Promessa Cli:</strong> data promessa al
                      cliente, calcolata partendo dalla{" "}
                      <strong>Data Conferma Fornitori</strong>e aggiungendo i
                      giorni di spedizione configurati per il fornitore.
                    </li>
                    <li>
                      <strong>Data Concordata Consegna:</strong> data di
                      consegna aggiornata lato cliente, calcolata partendo dalla
                      <strong> Data Effettiva Fornitori</strong> e aggiungendo i
                      giorni di spedizione del fornitore.
                    </li>
                  </ul>
                </Col>
              </Row>

              <div
                className="mt-3 p-2 px-3 rounded-3 bg-primary-subtle border border-primary-subtle"
                style={{ fontSize: "0.78rem", lineHeight: 1.5 }}
              >
                <strong>Esempio:</strong> se il fornitore conferma la
                disponibilità per il 10/09 e sono previsti 2 giorni di
                spedizione, la
                <strong> Data Promessa Cli</strong> sarà il 12/09. Se
                successivamente il fornitore aggiorna la propria data effettiva
                al 13/09, la
                <strong> Data Concordata Consegna</strong> diventerà il 15/09.
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer className="bg-white border-top px-4 py-3 d-flex justify-content-between">
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
              Suggerimento: usa prima ricerca e semaforo, poi affina il
              risultato con il pannello Filtri.
            </div>
            <Button variant="light" onClick={() => setShowInfoModal(false)}>
              Chiudi
            </Button>
          </Modal.Footer>
        </Modal>
        {/* OFFCANVAS FILTRI */}
        <Offcanvas
          show={showFiltersCanvas}
          onHide={() => setShowFiltersCanvas(false)}
          placement="end"
          className="orders-filter-canvas"
          backdropClassName="orders-filter-backdrop"
          style={{
            borderRadius: "16px 0 0 16px",
            zIndex: 20000,
          }}
        >
          <Offcanvas.Header closeButton className="border-bottom bg-light">
            <Offcanvas.Title
              className="fw-bold d-flex align-items-center gap-2"
              style={{ fontSize: "1.05rem" }}
            >
              <FiSliders className="text-primary" /> Filtri Ordini Cliente
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="d-flex flex-column justify-content-between p-4">
            <div className="d-flex flex-column gap-4">
              {/* Range Date Ordine Cliente */}
              <div>
                <div className="canvas-section-title d-flex align-items-center gap-1">
                  <Calendar size={14} /> Data Ordine Cliente
                </div>
                <Row className="g-2">
                  <Col>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={dateRange.start}
                      onChange={(e) =>
                        setDateRange({ ...dateRange, start: e.target.value })
                      }
                    />
                  </Col>
                  <Col>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={dateRange.end}
                      onChange={(e) =>
                        setDateRange({ ...dateRange, end: e.target.value })
                      }
                    />
                  </Col>
                </Row>
              </div>

              {/* Stato Calcolato */}
              <div>
                {/* SEZIONE QUANTITÀ CLIENTE */}

                <div className="canvas-section-title">Stato Avanzamento</div>
                <Form.Select
                  size="sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ fontWeight: "600" }}
                >
                  <option value="TUTTI">TUTTI GLI STATI</option>
                  <option value="ORDINATO">ORDINATO</option>
                  <option value="RIMESSO">RIMESSO</option>
                  <option value="ENTRATO IN MAGAZZINO">
                    ENTRATO IN MAGAZZINO
                  </option>
                  <option value="BEM INVIATA">BEM INVIATA</option>
                  <option value="BEM COMPLETATA">BEM COMPLETATA</option>
                  <option value="INVIO ODP/ODPV">INVIO ODP/ODPV</option>
                  <option value="COMPLETAMENTO ODP/ODPV">
                    COMPL. ODP/ODPV
                  </option>
                  <option value="EVASO">EVASO</option>
                </Form.Select>
              </div>

              {/* Cliente assegnato */}
              <div>
                <div className="canvas-section-title d-flex align-items-center gap-1">
                  <User size={14} /> Cliente Assegnato
                </div>
                <Form.Check
                  type="checkbox"
                  id="filter-only-no-client"
                  label="Mostra solo ordini senza cliente"
                  checked={onlyNoClient}
                  onChange={(e) => {
                    setOnlyNoClient(e.target.checked);
                    if (e.target.checked) setHideNoClient(false);
                  }}
                  className="mb-2 fw-semibold"
                />
                <Form.Check
                  type="checkbox"
                  id="filter-hide-no-client"
                  label="Nascondi ordini senza cliente"
                  checked={hideNoClient}
                  onChange={(e) => {
                    setHideNoClient(e.target.checked);
                    if (e.target.checked) setOnlyNoClient(false);
                  }}
                  className="fw-semibold"
                />
              </div>

              {/* Evasi parziali */}
              <div>
                <div className="canvas-section-title d-flex align-items-center gap-1">
                  <CheckCircle size={14} /> Evasi parziali
                </div>
                <Form.Check
                  type="checkbox"
                  id="filter-partial-evaded"
                  label="Evasi parziali"
                  checked={partialEvadedFilter}
                  onChange={(e) => setPartialEvadedFilter(e.target.checked)}
                  className="fw-semibold"
                />
                <div
                  className="text-muted mt-1"
                  style={{ fontSize: "0.72rem", lineHeight: 1.35 }}
                >
                  Se attivo, nasconde gli ordini con Da Evadere cliente e
                  fornitore entrambi a 0.
                </div>
              </div>

              {/* Causale */}
              <div>
                <div className="canvas-section-title d-flex align-items-center gap-1">
                  <FileText size={14} /> Causale
                </div>
                <Form.Check
                  type="checkbox"
                  id="filter-only-causale"
                  label="Mostra solo ordini con causale"
                  checked={onlyWithCausale}
                  onChange={(e) => setOnlyWithCausale(e.target.checked)}
                  className="fw-semibold"
                />
              </div>
            </div>

            <div className="d-flex gap-2 border-top pt-3 bg-white">
              <Button
                variant="outline-secondary"
                size="sm"
                className="w-50"
                onClick={resetFilters}
              >
                Reset
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="w-50 fw-bold"
                onClick={async () => {
                  // Ricarica realmente i dati dal PHP usando l'intervallo scelto.
                  // Non applichiamo nessuna esclusione aggiuntiva lato React:
                  // le esclusioni restano quelle definite nella query SQL.
                  await loadOrders(dateRange.start, dateRange.end);
                  setShowFiltersCanvas(false);
                }}
              >
                Applica
              </Button>
            </div>
          </Offcanvas.Body>
        </Offcanvas>
        {/* =========================================================
          SCELTA DESTINATARIO MAIL
      ========================================================= */}
        <Modal
          show={showMailChoiceModal}
          onHide={() => setShowMailChoiceModal(false)}
          centered
          className="mail-choice-modal"
          style={{ zIndex: 1095 }}
        >
          <Modal.Header closeButton>
            <div className="d-flex align-items-center gap-3">
              <div className="order-detail-icon-shell">
                <Mail size={20} strokeWidth={2.1} />
              </div>
              <div>
                <div className="mail-choice-eyebrow">Comunicazione ordine</div>
                <Modal.Title style={{ fontSize: "1rem" }}>
                  A chi vuoi inviare la mail?
                </Modal.Title>
              </div>
            </div>
          </Modal.Header>

          <Modal.Body>
            <div className="mail-choice-grid">
              <button
                type="button"
                className="mail-choice-card"
                onClick={() => openMailComposer("supplier")}
              >
                <Building size={21} />
                <div>
                  <strong>Fornitori</strong>
                  <span>Richiedi aggiornamento ordine e consegna</span>
                </div>
              </button>

              <button
                type="button"
                className="mail-choice-card"
                onClick={() => openMailComposer("client")}
                disabled={!mailChoiceOrder?.["ID Cliente"]}
              >
                <User size={21} />
                <div>
                  <strong>Clienti</strong>
                  <span>Avvisa il cliente del ritardo</span>
                </div>
              </button>

              <button
                type="button"
                className="mail-choice-card"
                onClick={() => openMailComposer("sales")}
              >
                <Send size={21} />
                <div>
                  <strong>Reparto vendite</strong>
                  <span>Sollecito verifica/completamento ODPV</span>
                </div>
              </button>
            </div>
          </Modal.Body>
        </Modal>
        {/* =========================================================
          MODALE MAIL FORNITORE
      ========================================================= */}
        <Modal
          show={showSupplierMailModal}
          onHide={() => {
            if (!supplierMailSending) setShowSupplierMailModal(false);
          }}
          centered
          className="supplier-mail-modal"
          style={{ zIndex: 1090 }}
        >
          <Modal.Header closeButton>
            <div className="d-flex align-items-center gap-3">
              <div className="order-detail-icon-shell">
                <Mail size={20} strokeWidth={2.1} />
              </div>

              <div>
                <div
                  style={{
                    color: GOLD,
                    fontSize: ".66rem",
                    fontWeight: 800,
                    letterSpacing: ".09em",
                    textTransform: "uppercase",
                  }}
                >
                  {mailRecipientType === "supplier"
                    ? "Comunicazione fornitore"
                    : mailRecipientType === "client"
                      ? "Comunicazione cliente"
                      : "Comunicazione reparto vendite"}
                </div>

                <Modal.Title style={{ fontSize: "1.05rem" }}>
                  {mailRecipientType === "supplier"
                    ? `Invia email a ${supplierMailOrder?.["Ragione sociale"] || "fornitore"} ordine ${supplierMailOrder?.["Tipo Doc"] || ""} ${supplierMailOrder?.["Num Doc"] || "N/D"}`
                    : mailRecipientType === "client"
                      ? `Avvisa ${supplierMailOrder?.["Rag Soc Cli"] || "cliente"} del ritardo - ordine ${supplierMailOrder?.["Tipo Ord Cli"] || ""} ${supplierMailOrder?.["Num Ord Cli"] || "N/D"}`
                      : `Sollecito ODPV - ordine ${supplierMailOrder?.["Tipo Ord Cli"] || ""} ${supplierMailOrder?.["Num Ord Cli"] || "N/D"}`}
                </Modal.Title>
              </div>
            </div>
          </Modal.Header>

          <Modal.Body className="p-4">
            <div className="supplier-mail-meta">
              <div className="supplier-mail-meta-card">
                <span className="supplier-mail-meta-label">Da</span>
                <span className="supplier-mail-meta-value supplier-mail-from">
                  ordini@technoparts.it
                </span>
              </div>

              <div className="supplier-mail-meta-card">
                <span className="supplier-mail-meta-label">Ccn / BCC</span>
                <span className="supplier-mail-meta-value">
                  {supplierMailLoading
                    ? "Ricerca email..."
                    : supplierEmails.length
                      ? supplierEmails.join("; ")
                      : "Nessuna email trovata"}
                </span>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Oggetto</Form.Label>
              <Form.Control
                type="text"
                value={supplierMailSubject}
                onChange={(e) => setSupplierMailSubject(e.target.value)}
                disabled={supplierMailSending}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Messaggio</Form.Label>
              <Form.Control
                as="textarea"
                rows={7}
                value={supplierMailMessage}
                onChange={(e) => setSupplierMailMessage(e.target.value)}
                disabled={supplierMailSending}
              />
            </Form.Group>

            {supplierMailError && (
              <div className="supplier-mail-alert error">
                {supplierMailError}
              </div>
            )}

            {supplierMailSuccess && (
              <div className="supplier-mail-alert success">
                {supplierMailSuccess}
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => setShowSupplierMailModal(false)}
              disabled={supplierMailSending}
            >
              Annulla
            </Button>

            <Button
              size="sm"
              className="supplier-mail-send-btn"
              onClick={sendSupplierMail}
              disabled={
                supplierMailSending ||
                supplierMailLoading ||
                !supplierEmails.length ||
                !supplierMailSubject.trim() ||
                !supplierMailMessage.trim()
              }
            >
              <Send size={14} />
              {supplierMailSending ? "Invio..." : "Invia mail"}
            </Button>
          </Modal.Footer>
        </Modal>
        {/* MODALE GIACENZE / PREZZI FORNITORI */}
        <Modal
          show={showGiacenzeModal}
          onHide={() => setShowGiacenzeModal(false)}
          centered
          dialogClassName={`giacenze-modal-dialog ${giacenzeTab === "fornitori" ? "giacenze-modal-dialog-wide" : ""}`}
          contentClassName="giacenze-modal-content"
        >
          <Modal.Header>
            <div className="d-flex align-items-center justify-content-between w-100 gap-3">
              <div className="orders-giacenze-title">
                <div className="orders-giacenze-icon"><Box size={22} /></div>
                <div>
                  <div className="orders-giacenze-eyebrow">Giacenze e prezzi fornitori</div>
                  <div className="orders-giacenze-code">{giacenzeArticolo}</div>
                  <div className="orders-giacenze-year">Anno {new Date().getFullYear()}</div>
                </div>
              </div>
              <button type="button" className="order-detail-close" onClick={() => setShowGiacenzeModal(false)} aria-label="Chiudi">
                <X size={17}/>
              </button>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="orders-article-tabs" role="tablist" aria-label="Dati articolo">
              <button type="button" role="tab" aria-selected={giacenzeTab === 'giacenze'}
                className={`orders-article-tab ${giacenzeTab === 'giacenze' ? 'active' : ''}`}
                onClick={() => switchGiacenzeTab('giacenze')}><Box size={15}/> Giacenze</button>
              <button type="button" role="tab" aria-selected={giacenzeTab === 'fornitori'}
                className={`orders-article-tab ${giacenzeTab === 'fornitori' ? 'active' : ''}`}
                onClick={() => switchGiacenzeTab('fornitori')}><Truck size={15}/> Fornitori</button>
            </div>
            {giacenzeTab === 'giacenze' ? (
              giacenzeLoading ? <div className="orders-giacenze-empty">Caricamento giacenze...</div>
              : giacenzeError ? <div className="orders-giacenze-empty text-danger">{giacenzeError}</div>
              : !giacenze.length ? <div className="orders-giacenze-empty">Nessuna giacenza trovata.</div>
              : <div className="orders-giacenze-panel">
                  <Table responsive className="orders-giacenze-table">
                    <thead><tr><th>Deposito</th><th className="text-end">Esistenza</th><th className="text-end">Ordinata</th><th className="text-end">Impegnata</th><th className="text-end">Disponibile</th></tr></thead>
                    <tbody>{giacenze.map((g, index) => <tr key={`${g.deposito ?? ''}-${index}`}>
                      <td><span className="orders-giacenze-deposito">{g.deposito}</span></td>
                      <td className="text-end orders-giacenze-value">{formatQuantita(g.esist_att)}</td>
                      <td className="text-end orders-giacenze-value">{formatQuantita(g.qta_ord)}</td>
                      <td className="text-end orders-giacenze-value">{formatQuantita(g.qta_imp)}</td>
                      <td className="text-end"><span className="orders-giacenze-disponibile">{formatQuantita(g.qta_disp)}</span></td>
                    </tr>)}</tbody>
                  </Table>
                </div>
            ) : (
              prezziLoading ? <div className="orders-giacenze-empty">Caricamento prezzi fornitori...</div>
              : prezziError ? <div className="orders-giacenze-empty text-danger">{prezziError} <button type="button" onClick={() => loadPrezziFornitori(articoloPrezzi)}>Riprova</button></div>
              : !prezziFornitori.length ? <div className="orders-giacenze-empty">Nessun prezzo fornitore disponibile.</div>
              : <div className="orders-fornitori-panel">
                  <div className="orders-fornitori-count">{prezziFornitori.length} fornitori · ordinati dal netto più basso</div>
                  <div className="orders-fornitori-scroll">
                    <Table responsive className="orders-giacenze-table orders-fornitori-table">
                      <thead><tr>
                        <th>Codice</th><th>Ragione sociale</th><th className="text-end">Qta stock</th>
                        <th>Data stock</th><th className="text-end">Lordo</th><th className="text-end">Sconto %</th>
                        <th className="text-end">Netto</th><th>Listino</th><th className="text-end">Netto Val</th>
                        <th>Val</th><th className="text-end">Cambio Euro</th><th>Ab.</th><th>Web</th><th>Stock</th><th>LTD</th><th>CC</th>
                      </tr></thead>
                      <tbody>{prezziFornitori.map((f, i) => <tr key={`${f.ID_Forn}-${i}`}>
                        <td>{f.ID_Forn ?? '—'}</td><td className="orders-fornitori-name">{f['Ragione Sociale'] || '—'}</td>
                        <td className="text-end">{formatQuantita(f['Qta Stock'])}</td><td>{f['Data Stock'] || '—'}</td>
                        <td className="text-end">{formatPrezzoFornitore(f.Lordo)}</td>
                        <td className="text-end">{formatPrezzoFornitore(f.Sconto)}</td>
                        <td className="text-end orders-fornitori-netto">{formatPrezzoFornitore(f.Netto)}</td>
                        <td>{f.Listino || '—'}</td><td className="text-end">{formatPrezzoFornitore(f['Netto Val'])}</td>
                        <td>{f.Val || '—'}</td><td className="text-end">{formatPrezzoFornitore(f['Cambio Euro'])}</td>
                        {['Ab.', 'Web', 'Stock', 'LTD', 'CC'].map(k => <td key={k} className="text-center">{f[k] == null ? '—' : (String(f[k]) === '1' || String(f[k]).toLowerCase() === 'true' ? '✓' : '—')}</td>)}
                      </tr>)}</tbody>
                    </Table>
                  </div>
                </div>
            )}
          </Modal.Body>
          <Modal.Footer><button type="button" className="orders-giacenze-close-btn" onClick={() => setShowGiacenzeModal(false)}>Chiudi</button></Modal.Footer>
        </Modal>

        {/* MODALE DETTAGLIO */}
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          centered
          size="xl"
          style={{ zIndex: 1070 }}
          className="orders-detail-modal"
          dialogClassName="orders-detail-dialog"
          contentClassName="orders-detail-content"
          backdropClassName="orders-detail-backdrop"
        >
          <Modal.Header className="order-detail-header">
            <div className="d-flex align-items-center justify-content-between gap-3 w-100 flex-wrap">
              <div className="order-detail-heading">
                <div className="order-detail-icon-shell">
                  <Box size={22} strokeWidth={2.1} />
                </div>
                <div className="min-w-0">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="order-detail-eyebrow">
                      Dettaglio ordine
                    </span>
                   <div className="d-inline-flex align-items-center gap-2">
  <span className="order-detail-article">
    {selectedOrder?.Articolo ||
      selectedOrder?.["Articolo cli"] ||
      "N/D"}
  </span>

  <button
    type="button"
    className="order-copy-btn"
    title="Copia codice articolo"
    onClick={(e) =>
      handleCopyArticle(
        e,
        selectedOrder?.Articolo ||
          selectedOrder?.["Articolo cli"]
      )
    }
  >
    {copiedArticle ===
    String(
      selectedOrder?.Articolo ||
        selectedOrder?.["Articolo cli"]
    ) ? (
      <Check size={14} color="#40c057" />
    ) : (
      <Copy size={14} />
    )}
  </button>
</div>
                  </div>
                  <h5 className="order-detail-description text-truncate">
                    {selectedOrder?.Descrizione || "Articolo senza descrizione"}
                  </h5>
                  <div className="order-detail-subline">
                    <span>
                      Cliente:{" "}
                      <strong>{selectedOrder?.["Num Ord Cli"] || "N/D"}</strong>
                    </span>
                    <span>•</span>
                    <span>
                      Fornitore:{" "}
                      <strong>{selectedOrder?.["Num Doc"] || "N/D"}</strong>
                    </span>
                  </div>
                </div>
              </div>

              <div className="order-detail-header-actions">
                <div
                  className="order-detail-state-pill"
                  style={{
                    color: getStatusStyle(currentStatus).color,
                    borderColor: `${getStatusStyle(currentStatus).color}38`,
                    background: `${getStatusStyle(currentStatus).color}12`,
                  }}
                >
                  {getStatusStyle(currentStatus).icon}
                  {currentStatus || "N/D"}
                </div>

                <div className="order-detail-delivery">
                  <Calendar size={14} />
                  <span>Consegna</span>
                  {renderDeliveryBadge(selectedOrder)}
                </div>

                <button
                  type="button"
                  className="order-detail-close"
                  onClick={() => setShowModal(false)}
                  aria-label="Chiudi dettaglio ordine"
                >
                  <X size={17} />
                </button>
              </div>
            </div>
          </Modal.Header>

          <Modal.Body className="order-detail-body">
            <section className="order-detail-section mb-3">
              <div className="order-detail-section-head">
                <div className="order-detail-section-title">
                  <span className="order-detail-section-icon">
                    <Truck size={15} />
                  </span>
                  Avanzamento logistico
                </div>
                <span className="order-detail-section-note">
                  Stato corrente: {currentStatus || "N/D"}
                </span>
              </div>

              <div className="order-timeline-grid">
                {selectedOrder && (
                  <>
                    {renderTimelineStep(
                      "1. Ordine",
                      selectedOrder["Data Ordine"],
                      `Doc: ${selectedOrder["Num Doc"] || ""}`,
                      currentStatus === "ORDINATO",
                    )}
                    {renderTimelineStep(
                      "2. Rimesso",
                      selectedOrder["Data Rimesso"],
                      selectedOrder["numero_rimesso"]
                        ? `N° ${selectedOrder["numero_rimesso"]}`
                        : "Elaborato",
                      currentStatus === "RIMESSO",
                    )}
                    {renderTimelineStep(
                      "3. Entrata Mag.",
                      selectedOrder["Data Entrata Magazzino"] ||
                        selectedOrder["data_entrata"],
                      selectedOrder["Num Entrata Magazzino"] ||
                        selectedOrder["numero_entrata"]
                        ? `N° ${selectedOrder["Num Entrata Magazzino"] || selectedOrder["numero_entrata"]}`
                        : "Registrato",
                      currentStatus === "ENTRATO IN MAGAZZINO",
                    )}
                    {renderTimelineStep(
                      "4. Invio BEM",
                      selectedOrder["bem_data_invio"],
                      `BEM: ${selectedOrder["id_bem"] || ""}`,
                      currentStatus === "BEM INVIATA",
                    )}
                    {renderTimelineStep(
                      "5. BEM Compl.",
                      selectedOrder["bem_data_copletamento"],
                      `BEM: ${selectedOrder["id_bem"] || ""}`,
                      currentStatus === "BEM COMPLETATA",
                    )}
                    {renderTimelineStep(
                      "6. Invio ODPV",
                      selectedOrder["Data Invio ODPV"],
                      selectedOrder["ODPV"] ? `${selectedOrder["ODPV"]}` : "",
                      currentStatus === "INVIO ODP/ODPV",
                    )}
                    {renderTimelineStep(
                      "7. Compl. ODPV",
                      selectedOrder["Data Completamento ODPV"],
                      selectedOrder["ODPV"] ? `${selectedOrder["ODPV"]}` : "",
                      currentStatus === "COMPLETAMENTO ODP/ODPV",
                    )}
                  </>
                )}
              </div>
            </section>

            <Row className="g-3 mb-3">
              <Col lg={6}>
                <section className="order-detail-section h-100">
                  <div className="order-detail-section-head">
                    <div className="order-detail-section-title">
                      <span className="order-detail-section-icon">
                        <User size={15} />
                      </span>
                      Dati cliente
                    </div>
                  </div>
                  <div className="order-detail-data-list">
                    <div className="order-detail-data-row">
                      <span className="order-detail-data-label">
                        <FileText size={13} /> Ordine cliente
                      </span>
                      <span className="order-detail-data-value">
                        {selectedOrder?.["Tipo Ord Cli"] || ""}{" "}
                        <span className="order-detail-code">
                          {selectedOrder?.["Num Ord Cli"] || "N/D"}
                        </span>
                      </span>
                    </div>
                    <div className="order-detail-data-row">
                      <span className="order-detail-data-label">
                        <User size={13} /> Cliente
                      </span>
                      <span className="order-detail-data-value">
                        {selectedOrder?.["Rag Soc Cli"] || "N/D"}{" "}
                        <span className="order-detail-code">
                          {selectedOrder?.["ID Cliente"] || "N/D"}
                        </span>
                      </span>
                    </div>
                    <div className="order-detail-data-row is-highlight">
                      <span className="order-detail-data-label">
                        <Calendar size={13} /> Data ordine cliente
                      </span>
                      <span className="order-detail-data-value">
                        {selectedOrder?.["Data Ordine cli"]
                          ? new Date(
                              selectedOrder["Data Ordine cli"].split(" ")[0],
                            ).toLocaleDateString("it-IT")
                          : "—"}
                      </span>
                    </div>
                    <div className="order-detail-data-row">
                      <span className="order-detail-data-label">
                        <Calendar size={13} /> Data promessa
                      </span>
                      <span className="order-detail-edit-wrap">
                        <span className="order-detail-data-value">
                          {selectedOrder?.["Data Promessa Cli"]
                            ? new Date(
                                selectedOrder["Data Promessa Cli"],
                              ).toLocaleDateString("it-IT")
                            : "—"}
                        </span>
                        <button
                          type="button"
                          className="order-detail-edit-btn"
                          onClick={() =>
                            setInlineEdit({
                              show: true,
                              field: "data_promessa_cli",
                              label: "Data Promessa",
                              value: selectedOrder?.["Data Promessa Cli"] || "",
                            })
                          }
                          aria-label="Modifica data promessa"
                        >
                          <Pencil size={11} />
                        </button>
                      </span>
                    </div>
                    <div className="order-detail-data-row">
                      <span className="order-detail-data-label">
                        <Calendar size={13} /> Data effettiva
                      </span>
                      <span className="order-detail-edit-wrap">
                        <span className="order-detail-data-value">
                          {selectedOrder?.["Data Concordata Consegna"]
                            ? new Date(
                                selectedOrder["Data Concordata Consegna"],
                              ).toLocaleDateString("it-IT")
                            : "—"}
                        </span>
                        <button
                          type="button"
                          className="order-detail-edit-btn"
                          onClick={() =>
                            setInlineEdit({
                              show: true,
                              field: "data_concordata_consegna",
                              label: "Data Concordata",
                              value:
                                selectedOrder?.["Data Concordata Consegna"] ||
                                "",
                            })
                          }
                          aria-label="Modifica data effettiva"
                        >
                          <Pencil size={11} />
                        </button>
                      </span>
                    </div>
                    <div className="order-detail-data-row">
                      <span className="order-detail-data-label">
                        <Calendar size={13} /> Data proposta
                      </span>
                      <span className="order-detail-edit-wrap">
                        <span className="order-detail-data-value">
                          {selectedOrder?.["Data proposta"]
                            ? new Date(
                                selectedOrder["Data proposta"],
                              ).toLocaleDateString("it-IT")
                            : "—"}
                        </span>
                        <button
                          type="button"
                          className="order-detail-edit-btn"
                          onClick={() =>
                            setInlineEdit({
                              show: true,
                              field: "data_proposta",
                              label: "Data proposta",
                              value: selectedOrder?.["Data proposta"] || "",
                            })
                          }
                          aria-label="Modifica data proposta"
                        >
                          <Pencil size={11} />
                        </button>
                      </span>
                    </div>
                  </div>
                </section>
              </Col>

              <Col lg={6}>
                <section className="order-detail-section h-100">
                  <div className="order-detail-section-head">
                    <div className="order-detail-section-title">
                      <span className="order-detail-section-icon">
                        <Building size={15} />
                      </span>
                      Dati fornitore
                    </div>
                  </div>
                  <div className="order-detail-data-list">
                    <div className="order-detail-data-row">
                      <span className="order-detail-data-label">
                        <FileText size={13} /> Ordine fornitore
                      </span>
                      <span className="order-detail-data-value">
                        {selectedOrder?.["Tipo Doc"] || ""}{" "}
                        <span className="order-detail-code">
                          {selectedOrder?.["Num Doc"] || "N/D"}
                        </span>
                      </span>
                    </div>
                    <div className="order-detail-data-row">
                      <span className="order-detail-data-label">
                        <Building size={13} /> Fornitore
                      </span>
                      <span className="order-detail-data-value">
                        {selectedOrder?.["Ragione sociale"] || "N/D"}{" "}
                        <span className="order-detail-code">
                          {selectedOrder?.["ID Fornitore"] || "N/D"}
                        </span>
                      </span>
                    </div>
                    <div className="order-detail-data-row is-highlight">
                      <span className="order-detail-data-label">
                        <Calendar size={13} /> Data ordine fornitore
                      </span>
                      <span className="order-detail-data-value">
                        {selectedOrder?.["Data Ordine"]
                          ? new Date(
                              selectedOrder["Data Ordine"].split(" ")[0],
                            ).toLocaleDateString("it-IT")
                          : "—"}
                      </span>
                    </div>
                    <div className="order-detail-data-row">
                      <span className="order-detail-data-label">
                        <Calendar size={13} /> Data conferma fornitore
                      </span>
                      <span className="order-detail-edit-wrap">
                        <span className="order-detail-data-value">
                          {selectedOrder?.["Data Conferma Fornitori"]
                            ? new Date(
                                selectedOrder["Data Conferma Fornitori"],
                              ).toLocaleDateString("it-IT")
                            : "—"}
                        </span>
                        <button
                          type="button"
                          className="order-detail-edit-btn"
                          onClick={() =>
                            setInlineEdit({
                              show: true,
                              field: "data_conferma_fornitori",
                              label: "Data Conferma Fornitore",
                              value:
                                selectedOrder?.["Data Conferma Fornitori"] ||
                                "",
                            })
                          }
                          aria-label="Modifica data conferma fornitore"
                        >
                          <Pencil size={11} />
                        </button>
                      </span>
                    </div>
                    <div className="order-detail-data-row">
                      <span className="order-detail-data-label">
                        <Calendar size={13} /> Data effettiva fornitore
                      </span>
                      <span className="order-detail-edit-wrap">
                        <span className="order-detail-data-value">
                          {selectedOrder?.["Data Effettiva Fornitori"]
                            ? new Date(
                                selectedOrder["Data Effettiva Fornitori"],
                              ).toLocaleDateString("it-IT")
                            : "—"}
                        </span>
                        <button
                          type="button"
                          className="order-detail-edit-btn"
                          onClick={() =>
                            setInlineEdit({
                              show: true,
                              field: "data_effettiva_fornitori",
                              label: "Data Effettiva Fornitore",
                              value:
                                selectedOrder?.["Data Effettiva Fornitori"] ||
                                "",
                            })
                          }
                          aria-label="Modifica data effettiva fornitore"
                        >
                          <Pencil size={11} />
                        </button>
                      </span>
                    </div>
                  </div>
                </section>
              </Col>
            </Row>

            {(saveMsg || savingDates) && (
              <div className="order-save-feedback">
                {savingDates ? (
                  <span className="order-save-pill is-loading">
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    Salvataggio in corso...
                  </span>
                ) : (
                  <span
                    className={`order-save-pill ${saveMsg.includes("✅") ? "is-success" : "is-error"}`}
                  >
                    {saveMsg}
                  </span>
                )}
              </div>
            )}

            <Row className="g-3">
              <Col lg={6}>
                <section className="order-detail-section h-100">
                  <div className="order-detail-section-head">
                    <div className="order-detail-section-title">
                      <span className="order-detail-section-icon">
                        <User size={15} />
                      </span>
                      Quantità cliente
                    </div>
                  </div>
                  <div className="order-quantity-grid">
                    <div className="order-kpi">
                      <div className="order-kpi-top">
                        <span className="order-kpi-label">Ordinata</span>
                        <span className="order-kpi-icon">
                          <Layers size={14} />
                        </span>
                      </div>
                      <div className="order-kpi-value">
                        {formatQuantita(qtaOrdinataCli)}
                        <small>pz</small>
                      </div>
                    </div>
                    <div className="order-kpi tone-warning">
                      <div className="order-kpi-top">
                        <span className="order-kpi-label">Da evadere</span>
                        <span className="order-kpi-icon">
                          <Clock size={14} />
                        </span>
                      </div>
                      <div className="order-kpi-value">
                        {formatQuantita(qtaDaEvadereCli)}
                        <small>pz</small>
                      </div>
                    </div>
                    <div className="order-kpi tone-success">
                      <div className="order-kpi-top">
                        <span className="order-kpi-label">Evaso</span>
                        <span className="order-kpi-icon">
                          <CheckCircle size={14} />
                        </span>
                      </div>
                      <div className="order-kpi-value">
                        {formatQuantita(qtaEvasoCli)}
                        <small>pz</small>
                      </div>
                    </div>
                  </div>
                </section>
              </Col>
              <Col lg={6}>
                <section className="order-detail-section h-100">
                  <div className="order-detail-section-head">
                    <div className="order-detail-section-title">
                      <span className="order-detail-section-icon">
                        <Building size={15} />
                      </span>
                      Quantità fornitore
                    </div>
                  </div>
                  <div className="order-quantity-grid">
                    <div className="order-kpi">
                      <div className="order-kpi-top">
                        <span className="order-kpi-label">Ordinata</span>
                        <span className="order-kpi-icon">
                          <Layers size={14} />
                        </span>
                      </div>
                      <div className="order-kpi-value">
                        {formatQuantita(qtaOrdinataFor)}
                        <small>pz</small>
                      </div>
                    </div>
                    <div className="order-kpi tone-warning">
                      <div className="order-kpi-top">
                        <span className="order-kpi-label">Da evadere</span>
                        <span className="order-kpi-icon">
                          <Clock size={14} />
                        </span>
                      </div>
                      <div className="order-kpi-value">
                        {formatQuantita(qtaDaEvadereFor)}
                        <small>pz</small>
                      </div>
                    </div>
                    <div className="order-kpi tone-success">
                      <div className="order-kpi-top">
                        <span className="order-kpi-label">Evaso</span>
                        <span className="order-kpi-icon">
                          <CheckCircle size={14} />
                        </span>
                      </div>
                      <div className="order-kpi-value">
                        {formatQuantita(qtaEvasoFor)}
                        <small>pz</small>
                      </div>
                    </div>
                    <div className="order-kpi tone-info">
                      <div className="order-kpi-top">
                        <span className="order-kpi-label">Rimesso</span>
                        <span className="order-kpi-icon">
                          <Truck size={14} />
                        </span>
                      </div>
                      <div className="order-kpi-value">
                        {formatQuantita(qtaRimessaFor)}
                        <small>pz</small>
                      </div>
                    </div>
                  </div>
                </section>
              </Col>
            </Row>
          </Modal.Body>
        </Modal>{" "}
      </div>
    </div>
  );
};

export { MonitoraggioOrdini, MonitoraggioOrdini as SalesChart };
export default MonitoraggioOrdini;